import { getApiUrl } from '../config/api';
import { authUtils } from '../utils/authUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Ship, 
  MessageCircle, 
  Key,
  TrendingUp,
  Copy,
  Check,
  Plus,
  X,
  Calendar,
  Eye,
  Shield,
  ArrowLeft,
  Brain,
  Play,
  Clock,
  FileText,
  Pencil,
  Upload,
  AlertCircle,
  Share2,
  ExternalLink,
  Send,
  Loader2,
  Coins,
  Repeat,
  Mail,
  Save,
  Trash2,
  Filter,
  Globe,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import RichTextEditor from '../components/RichTextEditor';

export default function Admin() {
  const navigate = useNavigate();
  
  // Get API URL at runtime
  const BACKEND_URL = getApiUrl();
  
  const [stats, setStats] = useState(null);
  const [countriesData, setCountriesData] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'users', 'codes', 'immersion', 'shared', 'diagnostics', 'student-view', 'token-usage', 'vessels'
  const [adminVessels, setAdminVessels] = useState(null);
  const [adminVesselsLoading, setAdminVesselsLoading] = useState(false);
  const [showOnlyEmptyVessels, setShowOnlyEmptyVessels] = useState(false);
  const [winbackSending, setWinbackSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Immersion state
  const [immersionStatus, setImmersionStatus] = useState(null);
  const [immersionLoading, setImmersionLoading] = useState(false);
  const [thematicCategories, setThematicCategories] = useState({});
  const [isCustomCategories, setIsCustomCategories] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', keywords: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedThemesForImmersion, setSelectedThemesForImmersion] = useState([]);
  const [uploadingChip, setUploadingChip] = useState(false);
  const [memoryChipsInfo, setMemoryChipsInfo] = useState(null);
  const [testingKeyUserId, setTestingKeyUserId] = useState(null);
  const [keyTestResults, setKeyTestResults] = useState({});
  const [sharedThreads, setSharedThreads] = useState([]);
  const [viewingThread, setViewingThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Diagnostics state
  const [diagEmail, setDiagEmail] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [adminList, setAdminList] = useState(null);
  const [duplicateAccounts, setDuplicateAccounts] = useState(null);
  const [mergingEmail, setMergingEmail] = useState(null);

  const mergeDuplicatesForEmail = async (email) => {
    // Confirmation is now inline via a clear toast — `window.confirm()` is
    // unreliable once Chrome's "prevent additional dialogs" has ever been
    // triggered on this site.
    setMergingEmail(email);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/merge-duplicate-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.no_change) {
          toast.info('Only one account exists — nothing to merge.', { duration: 6000 });
        } else {
          const repointed = Object.entries(data.repointed || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
          toast.success(
            `Merged ${data.removed_count} duplicate(s) for ${email}. Repointed → ${repointed || 'no foreign keys'}.`,
            { duration: 10000 }
          );
        }
        const dupRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/duplicate-accounts`);
        if (dupRes.ok) setDuplicateAccounts(await dupRes.json());
      } else {
        let detailMsg = `Merge failed (HTTP ${res.status})`;
        try {
          const err = await res.json();
          if (typeof err.detail === 'string') detailMsg = err.detail;
          else if (Array.isArray(err.detail)) detailMsg = err.detail.map(d => d.msg || JSON.stringify(d)).join('; ');
          else if (err.detail) detailMsg = JSON.stringify(err.detail);
        } catch (_) { /* keep default */ }
        console.error('Merge failed:', res.status, detailMsg);
        toast.error(detailMsg, { duration: 10000 });
      }
    } catch (err) {
      console.error('Merge request threw:', err);
      toast.error(`Merge request failed: ${err?.message || err}`, { duration: 10000 });
    } finally {
      setMergingEmail(null);
    }
  };

  // Vessel Thread Recovery (admin-only) — finds threads that may be hidden
  // from a user's regular vessel UI due to user_id mismatches.
  const [recoveryScope, setRecoveryScope] = useState('vessel'); // 'vessel' | 'sanctuary'
  const [recoveryVessel, setRecoveryVessel] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryDays, setRecoveryDays] = useState(7);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryExpanded, setRecoveryExpanded] = useState({}); // conv_id → bool
  const [recoveryReattaching, setRecoveryReattaching] = useState({}); // conv_id → bool
  const [myVessels, setMyVessels] = useState([]);
  const [moveToVesselId, setMoveToVesselId] = useState('');  // empty = don't repoint

  // Fetch the admin's vessels once so the recovery panel can offer a
  // "move to this vessel" picker (vessel_id drift recovery).
  useEffect(() => {
    (async () => {
      try {
        const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/my-vessels`);
        if (res.ok) {
          const d = await res.json();
          const vessels = d.vessels || [];
          setMyVessels(vessels);
          // Smart default: prefer the vessel the admin was just viewing
          // (sessionStorage from VesselChat), else the most-recently-used vessel.
          const lastViewed = (() => {
            try { return sessionStorage.getItem('last_viewed_vessel_id') || ''; }
            catch (_) { return ''; }
          })();
          const matched = vessels.find((v) => v.vessel_id === lastViewed);
          if (matched) {
            setMoveToVesselId(matched.vessel_id);
          } else if (vessels.length > 0) {
            setMoveToVesselId(vessels[0].vessel_id);
          }
        }
      } catch (_) { /* non-critical */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restoreThreadToCurrentUser = async (conversationId) => {
    // Note: previously used window.confirm() which Chrome can permanently
    // suppress per-tab ("Prevent additional dialogs" checkbox) — once that's
    // checked, confirm() silently returns false and the restore looks like
    // it does nothing while a brief yellow Chrome bar flashes. We use a
    // persistent toast instead so the result is always visible.
    setRecoveryReattaching((p) => ({ ...p, [conversationId]: true }));
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessel-thread-recovery/${conversationId}/reattach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: recoveryScope,
          ...(recoveryScope === 'vessel' && moveToVesselId ? { target_vessel_id: moveToVesselId } : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.no_change) {
          toast.info('Thread is already attached to your account and vessel.', { duration: 6000 });
        } else {
          const parts = [];
          if (data.modified) parts.push('user_id restored');
          if (data.vessel_repointed) parts.push('moved to selected vessel');
          if (data.messages_reattached) parts.push(`${data.messages_reattached} messages reattached`);
          toast.success(
            `Restored: ${parts.join(' · ') || 'thread updated'}.`,
            { duration: 8000 }
          );
        }
        runVesselRecovery(false);
      } else {
        let detailMsg = `Restore failed (HTTP ${res.status})`;
        try {
          const err = await res.json();
          if (typeof err.detail === 'string') detailMsg = err.detail;
          else if (Array.isArray(err.detail)) detailMsg = err.detail.map(d => d.msg || JSON.stringify(d)).join('; ');
          else if (err.detail) detailMsg = JSON.stringify(err.detail);
        } catch (_) { /* keep default */ }
        console.error('Restore failed:', res.status, detailMsg);
        toast.error(detailMsg, { duration: 10000 });
      }
    } catch (err) {
      console.error('Restore request threw:', err);
      toast.error(`Restore request failed: ${err?.message || err}`, { duration: 10000 });
    } finally {
      setRecoveryReattaching((p) => ({ ...p, [conversationId]: false }));
    }
  };

  const runVesselRecovery = async (withMessages = false) => {
    setRecoveryLoading(true);
    setRecoveryResult(null);
    try {
      const params = new URLSearchParams();
      if (recoveryVessel) params.set('vessel_name_contains', recoveryVessel);
      if (recoveryEmail) params.set('user_email', recoveryEmail);
      params.set('days', String(recoveryDays || 7));
      params.set('scope', recoveryScope);
      if (withMessages) params.set('include_messages', 'true');
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessel-thread-recovery?${params.toString()}`);
      if (res.ok) {
        setRecoveryResult(await res.json());
      } else {
        toast.error(`Recovery failed (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error('Recovery request failed');
    } finally {
      setRecoveryLoading(false);
    }
  };
  const [uploadDiag, setUploadDiag] = useState(null);
  const [uploadDiagLoading, setUploadDiagLoading] = useState(false);

  const checkUserUploads = async () => {
    if (!diagEmail) return;
    setUploadDiagLoading(true);
    setUploadDiag(null);
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/users/${encodeURIComponent(diagEmail)}/uploads`
      );
      if (res.ok) {
        setUploadDiag(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload diagnostic failed');
      }
    } catch (err) {
      toast.error('Upload diagnostic failed');
    } finally {
      setUploadDiagLoading(false);
    }
  };

  // Verbatim-repeat monitor
  const [repeatData, setRepeatData] = useState(null);
  const [repeatLoading, setRepeatLoading] = useState(false);
  const [repeatWindowDays, setRepeatWindowDays] = useState(7);

  // Broadcast composer
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [currentBroadcast, setCurrentBroadcast] = useState(null); // {broadcast_id?, subject, body_html, filters, status?}
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastFilters, setBroadcastFilters] = useState({
    api_key: 'any',
    active_since_days: 0,
    signed_up_within_days: 0,
    exclude_admin: true,
    only_emails: '',
  });
  const [broadcastSaving, setBroadcastSaving] = useState(false);
  const [broadcastTestSending, setBroadcastTestSending] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastPreview, setBroadcastPreview] = useState(null); // {count, sample}
  const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendConfirmText, setSendConfirmText] = useState('');

  const buildFiltersPayload = () => ({
    api_key: broadcastFilters.api_key,
    active_since_days: parseInt(broadcastFilters.active_since_days, 10) || 0,
    signed_up_within_days: parseInt(broadcastFilters.signed_up_within_days, 10) || 0,
    exclude_admin: !!broadcastFilters.exclude_admin,
    only_emails: (broadcastFilters.only_emails || '')
      .split(/[,\n\s]+/).map((s) => s.trim()).filter(Boolean),
  });

  const resetBroadcastForm = () => {
    setCurrentBroadcast(null);
    setBroadcastSubject('');
    setBroadcastBody('');
    setBroadcastFilters({
      api_key: 'any',
      active_since_days: 0,
      signed_up_within_days: 0,
      exclude_admin: true,
      only_emails: '',
    });
    setBroadcastPreview(null);
  };

  const fetchBroadcasts = async () => {
    setBroadcastsLoading(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/broadcasts`);
      if (res.ok) setBroadcasts(await res.json());
    } catch (err) {
      console.error('Broadcasts list error:', err);
    } finally {
      setBroadcastsLoading(false);
    }
  };

  const loadBroadcast = (b) => {
    setCurrentBroadcast(b);
    setBroadcastSubject(b.subject || '');
    setBroadcastBody(b.body_html || '');
    const f = b.filters || {};
    setBroadcastFilters({
      api_key: f.api_key || 'any',
      active_since_days: f.active_since_days || 0,
      signed_up_within_days: f.signed_up_within_days || 0,
      exclude_admin: f.exclude_admin !== false,
      only_emails: Array.isArray(f.only_emails) ? f.only_emails.join('\n') : (f.only_emails || ''),
    });
    setBroadcastPreview(null);
  };

  const saveBroadcastDraft = async () => {
    if (!broadcastSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    setBroadcastSaving(true);
    try {
      const payload = {
        subject: broadcastSubject.trim(),
        body_html: broadcastBody,
        filters: buildFiltersPayload(),
      };
      const url = currentBroadcast?.broadcast_id
        ? `${BACKEND_URL}/api/admin/broadcasts/${currentBroadcast.broadcast_id}`
        : `${BACKEND_URL}/api/admin/broadcasts`;
      const method = currentBroadcast?.broadcast_id ? 'PATCH' : 'POST';
      const res = await authUtils.authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json();
        setCurrentBroadcast(saved);
        toast.success('Draft saved');
        fetchBroadcasts();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save draft');
      }
    } catch (err) {
      console.error('Save broadcast error:', err);
      toast.error('Failed to save draft');
    } finally {
      setBroadcastSaving(false);
    }
  };

  const deleteBroadcast = async (broadcastId) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/broadcasts/${broadcastId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Draft deleted');
        if (currentBroadcast?.broadcast_id === broadcastId) resetBroadcastForm();
        fetchBroadcasts();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (err) {
      console.error('Delete broadcast error:', err);
      toast.error('Failed to delete');
    }
  };

  const previewRecipients = async () => {
    // Ensure we have a saved draft first (preview API needs an id)
    if (!currentBroadcast?.broadcast_id) {
      toast('Save the draft first to preview recipients', { icon: 'ℹ️' });
      return;
    }
    setBroadcastPreviewLoading(true);
    try {
      // Save filters first to ensure preview reflects what's on screen
      await saveBroadcastDraft();
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/broadcasts/${currentBroadcast.broadcast_id}/preview-recipients`,
        { method: 'POST' }
      );
      if (res.ok) {
        setBroadcastPreview(await res.json());
      } else {
        toast.error('Failed to preview recipients');
      }
    } catch (err) {
      console.error('Preview recipients error:', err);
      toast.error('Failed to preview recipients');
    } finally {
      setBroadcastPreviewLoading(false);
    }
  };

  const sendBroadcastTest = async () => {
    if (!currentBroadcast?.broadcast_id) {
      toast('Save the draft first', { icon: 'ℹ️' });
      return;
    }
    setBroadcastTestSending(true);
    try {
      await saveBroadcastDraft();
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/broadcasts/${currentBroadcast.broadcast_id}/send-test`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Test sent to ${data.recipient}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Test send failed');
      }
    } catch (err) {
      console.error('Send test error:', err);
      toast.error('Test send failed');
    } finally {
      setBroadcastTestSending(false);
    }
  };

  const confirmAndSendBroadcast = async () => {
    if ((sendConfirmText || '').trim().toUpperCase() !== 'SEND') {
      toast.error('Type SEND to confirm');
      return;
    }
    if (!currentBroadcast?.broadcast_id) return;
    setBroadcastSending(true);
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/broadcasts/${currentBroadcast.broadcast_id}/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirm: 'SEND' }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Broadcast queued for ${data.recipient_count} recipients`);
        setShowSendConfirm(false);
        setSendConfirmText('');
        resetBroadcastForm();
        fetchBroadcasts();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Broadcast failed');
      }
    } catch (err) {
      console.error('Send broadcast error:', err);
      toast.error('Broadcast failed');
    } finally {
      setBroadcastSending(false);
    }
  };

  const fetchVerbatimRepeats = async (days = repeatWindowDays) => {
    setRepeatLoading(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/verbatim-repeats?days=${days}`);
      if (res.ok) {
        setRepeatData(await res.json());
      } else {
        toast.error('Could not load repeat events');
      }
    } catch (err) {
      console.error('Verbatim repeats fetch error:', err);
      toast.error('Could not load repeat events');
    } finally {
      setRepeatLoading(false);
    }
  };
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [keyAttempts, setKeyAttempts] = useState(null);
  const [keyAttemptsLoading, setKeyAttemptsLoading] = useState(false);
  
  // Student View state
  const [studentViewEmail, setStudentViewEmail] = useState('');
  const [studentViewData, setStudentViewData] = useState(null);
  const [studentViewLoading, setStudentViewLoading] = useState(false);
  const [expandedConv, setExpandedConv] = useState(null);
  // Full-transcript modal (for recovering guest conversations)
  const [fullTranscript, setFullTranscript] = useState(null);
  const [fullTranscriptLoading, setFullTranscriptLoading] = useState(false);
  // Memory audit modal (for pruning confabulated memories)
  const [memoryAudit, setMemoryAudit] = useState(null);          // {user_id, memories, counts_by_type}
  const [memoryAuditLoading, setMemoryAuditLoading] = useState(false);
  const [memorySearchQ, setMemorySearchQ] = useState('');
  const [memoryFilterType, setMemoryFilterType] = useState('');
  const [memorySelected, setMemorySelected] = useState(new Set());
  // Verified persons (ground truth Alvin must check before asserting)
  const [verifiedPersons, setVerifiedPersons] = useState(null);   // array or null
  const [verifiedSaving, setVerifiedSaving] = useState(false);
  // Pending corrections (one-shot directives Alvin delivers at target user's next chat)
  const [pendingCorrections, setPendingCorrections] = useState(null);
  const [newCorrectionText, setNewCorrectionText] = useState('');
  const [newCorrectionScope, setNewCorrectionScope] = useState('sanctuary');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  
  // Token Usage state
  const [tokenUsageData, setTokenUsageData] = useState(null);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(false);
  const [tokenUsageDays, setTokenUsageDays] = useState(30);
  const [selectedUserUsage, setSelectedUserUsage] = useState(null);
  const [userDailyBreakdown, setUserDailyBreakdown] = useState(null);
  const [userDailyLoading, setUserDailyLoading] = useState(false);
  
  // Create invitation code form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    max_uses: 1,
    expires_in_days: 30
  });
  
  useEffect(() => {
    fetchAdminData(true);
  }, []);
  
  const fetchAdminData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setIsLoading(true);
      
      // Fetch stats
      const statsRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/stats`);
      
      if (statsRes.status === 403) {
        toast.error('Access denied - Admin only');
        navigate('/sanctuary');
        return;
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch countries aggregation (privacy-respectful: stored timezone → country)
      try {
        const countriesRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/countries`);
        if (countriesRes.ok) {
          setCountriesData(await countriesRes.json());
        }
      } catch (_) { /* non-critical */ }

      // Fetch configured admin list (single source of truth, /api/admin/admins)
      try {
        const adminRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/admins`);
        if (adminRes.ok) {
          setAdminList(await adminRes.json());
        }
      } catch (_) { /* non-critical */ }

      // Fetch duplicate-account detector for the merge UI
      try {
        const dupRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/duplicate-accounts`);
        if (dupRes.ok) {
          setDuplicateAccounts(await dupRes.json());
        }
      } catch (_) { /* non-critical */ }
      
      // Fetch users
      const usersRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/users`);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }
      
      // Fetch invitation codes
      const codesRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/invitation-codes`);
      if (codesRes.ok) {
        const codesData = await codesRes.json();
        setInvitationCodes(codesData);
      }
      
      // Fetch immersion status
      const immersionRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/status`);
      if (immersionRes.ok) {
        const immersionData = await immersionRes.json();
        setImmersionStatus(immersionData);
      }
      
      // Fetch thematic categories
      const categoriesRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/thematic-categories`);
      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        setThematicCategories(categoriesData.categories || {});
        setIsCustomCategories(categoriesData.is_custom || false);
      }
      
      // Fetch memory chips info
      const chipsRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/check-chips`);
      if (chipsRes.ok) {
        const chipsData = await chipsRes.json();
        setMemoryChipsInfo(chipsData);
      }
      
      // Fetch shared threads
      const sharedRes = await authUtils.authFetch(`${BACKEND_URL}/api/admin/shared-threads`);
      if (sharedRes.ok) {
        const sharedData = await sharedRes.json();
        setSharedThreads(sharedData);
      }
      
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendReply = async (conversationId) => {
    if (!replyText.trim() || sendingReply) return;
    
    setSendingReply(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/shared-threads/${conversationId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyText.trim() })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Add the note to the current thread view
        setViewingThread(prev => ({
          ...prev,
          messages: [...(prev.messages || []), data.note]
        }));
        setReplyText('');
        toast.success('Note sent! Student will be notified by email.');
        
        // Scroll to bottom
        setTimeout(() => {
          const container = document.getElementById('thread-messages');
          if (container) container.scrollTop = container.scrollHeight;
        }, 100);
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };
  
  const handleCreateCode = async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/invitation-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCode)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Invitation code created!');
        setShowCreateForm(false);
        setNewCode({ code: '', max_uses: 1, expires_in_days: 30 });
        fetchAdminData();
        
        // Auto-copy the new code
        navigator.clipboard.writeText(data.code);
        toast.success('Code copied to clipboard');
      } else {
        toast.error('Failed to create code');
      }
    } catch (error) {
      console.error('Failed to create code:', error);
      toast.error('Error creating code');
    }
  };
  
  const handleDeactivateCode = async (code) => {
    if (!window.confirm('Deactivate this invitation code?')) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/invitation-codes/${code}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Code deactivated');
        fetchAdminData();
      } else {
        toast.error('Failed to deactivate code');
      }
    } catch (error) {
      console.error('Failed to deactivate code:', error);
      toast.error('Error deactivating code');
    }
  };
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };
  
  const exportEmails = () => {
    const emails = users.map(u => u.email).join('\n');
    navigator.clipboard.writeText(emails);
    toast.success(`${users.length} emails copied to clipboard!`);
  };
  
  const testUserApiKey = async (userId) => {
    setTestingKeyUserId(userId);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/users/${userId}/test-api-key`, {
        method: 'POST'
      });
      
      const data = await res.json();
      setKeyTestResults(prev => ({ ...prev, [userId]: data }));
      
      if (data.status === 'valid') {
        toast.success(`${data.user_name}'s API key is valid and has credits`);
      } else if (data.status === 'no_key') {
        toast.warning(`${data.user_name} has not configured an API key`);
      } else if (data.status === 'invalid') {
        toast.error(`${data.user_name}'s API key is invalid or revoked`);
      } else if (data.status === 'no_credits') {
        toast.error(`${data.user_name}'s API key has no credits remaining`);
      } else if (data.status === 'rate_limited') {
        toast.warning(`${data.user_name}'s API key is rate limited`);
      } else {
        toast.error(`Error testing key: ${data.message}`);
      }
    } catch (error) {
      toast.error('Failed to test API key');
    } finally {
      setTestingKeyUserId(null);
    }
  };

  const downloadEmailsCSV = () => {
    const csvContent = [
      ['Name', 'Email', 'Joined', 'Vessels', 'Onboarding', 'API Key'].join(','),
      ...users.map(u => [
        `"${u.name}"`,
        u.email,
        new Date(u.created_at).toLocaleDateString(),
        u.vessel_count || 0,
        u.has_completed_onboarding ? 'Complete' : 'Pending',
        u.has_api_key ? 'Set' : 'None'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };
  
  const fetchStudentView = async (userId) => {
    setStudentViewLoading(true);
    setStudentViewData(null);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/view-as-student/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setStudentViewData(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load student view');
      }
    } catch (error) {
      toast.error('Failed to load student view');
    } finally {
      setStudentViewLoading(false);
    }
  };
  
  const viewStudentByEmail = async () => {
    if (!studentViewEmail.trim()) return;
    const student = users.find(u => u.email.toLowerCase() === studentViewEmail.trim().toLowerCase());
    if (student) {
      fetchStudentView(student.user_id);
    } else {
      toast.error('Student not found. Check the email address.');
    }
  };
  
  const loadFullTranscript = async (conversationId) => {
    setFullTranscriptLoading(true);
    setFullTranscript(null);
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/conversations/${conversationId}/full-transcript`
      );
      if (res.ok) {
        const data = await res.json();
        setFullTranscript(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load transcript');
      }
    } catch (error) {
      toast.error('Failed to load full transcript');
    } finally {
      setFullTranscriptLoading(false);
    }
  };
  
  const downloadTranscriptTxt = () => {
    if (!fullTranscript) return;
    const owner = fullTranscript.owner || {};
    const conv = fullTranscript.conversation || {};
    const header = [
      `Alvin's Place — Full Transcript`,
      `User: ${owner.name || 'Unknown'} <${owner.email || 'unknown'}>`,
      `Conversation: ${conv.title || conv.conversation_id}`,
      `Created: ${conv.created_at || ''}`,
      `Messages: ${fullTranscript.message_count}`,
      `Interrupted/partial rows: ${fullTranscript.partial_or_interrupted_count}`,
      '',
      '─'.repeat(60),
      '',
    ].join('\n');
    const body = (fullTranscript.messages || []).map(m => {
      const role = m.role === 'alvin' ? 'ALVIN' : (m.role || 'user').toUpperCase();
      const flags = [];
      if (m.is_partial) flags.push('PARTIAL');
      if (m.interrupted) flags.push('INTERRUPTED');
      const flagStr = flags.length ? ` [${flags.join(', ')}]` : '';
      return `[${m.created_at || ''}] ${role}${flagStr}:\n${m.content || ''}\n`;
    }).join('\n');
    const blob = new Blob([header + body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alvins-place-transcript-${(owner.email || 'unknown').replace(/[^a-z0-9]/gi,'_')}-${(conv.conversation_id || 'x').slice(0,8)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  
  const loadMemoryAudit = async (userId, opts = {}) => {
    setMemoryAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (opts.q ?? memorySearchQ) params.set('q', opts.q ?? memorySearchQ);
      if (opts.memory_type ?? memoryFilterType) params.set('memory_type', opts.memory_type ?? memoryFilterType);
      params.set('limit', '300');
      // Fetch memories + verified persons + this user's email (so we can filter corrections)
      const [memRes, vpRes, userRes] = await Promise.all([
        authUtils.authFetch(`${BACKEND_URL}/api/admin/users/${userId}/memories?${params.toString()}`),
        authUtils.authFetch(`${BACKEND_URL}/api/admin/users/${userId}/verified-persons`),
        authUtils.authFetch(`${BACKEND_URL}/api/admin/view-as-student/${userId}`),
      ]);
      let userEmail = null;
      if (userRes.ok) {
        const ud = await userRes.json();
        userEmail = ud?.student?.email || null;
      }
      if (memRes.ok) {
        const data = await memRes.json();
        setMemoryAudit({ ...data, target_email: userEmail });
        setMemorySelected(new Set());
      } else {
        toast.error('Failed to load memories');
      }
      if (vpRes.ok) {
        const vpData = await vpRes.json();
        setVerifiedPersons(vpData.persons || []);
      } else {
        setVerifiedPersons([]);
      }
      // Fetch pending corrections targeted at this user's email
      if (userEmail) {
        const corrRes = await authUtils.authFetch(
          `${BACKEND_URL}/api/admin/pending-corrections?include_delivered=true&email=${encodeURIComponent(userEmail)}`
        );
        if (corrRes.ok) {
          const cd = await corrRes.json();
          setPendingCorrections(cd.corrections || []);
        } else {
          setPendingCorrections([]);
        }
      } else {
        setPendingCorrections([]);
      }
    } catch {
      toast.error('Failed to load memories');
    } finally {
      setMemoryAuditLoading(false);
    }
  };
  
  const updateVerifiedPerson = (idx, field, value) => {
    setVerifiedPersons(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  
  const addVerifiedPerson = () => {
    setVerifiedPersons(prev => [...(prev || []), { name: '', role: '', status: 'alive', notes: '' }]);
  };
  
  const removeVerifiedPerson = (idx) => {
    setVerifiedPersons(prev => prev.filter((_, i) => i !== idx));
  };
  
  const saveVerifiedPersons = async () => {
    if (!memoryAudit || verifiedPersons === null) return;
    setVerifiedSaving(true);
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/admin/users/${memoryAudit.user_id}/verified-persons`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ persons: verifiedPersons.filter(p => (p.name || '').trim()) }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setVerifiedPersons(data.persons || []);
        toast.success(`Saved ${data.saved} verified person${data.saved === 1 ? '' : 's'}.`);
      } else {
        toast.error('Failed to save verified persons');
      }
    } catch {
      toast.error('Failed to save verified persons');
    } finally {
      setVerifiedSaving(false);
    }
  };
  
  const queueCorrection = async () => {
    if (!memoryAudit || !memoryAudit.target_email) {
      toast.error('Cannot queue — this user has no email on file.');
      return;
    }
    const text = (newCorrectionText || '').trim();
    if (text.length < 10) {
      toast.error('Correction text must be at least 10 characters.');
      return;
    }
    setCorrectionSaving(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/pending-corrections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_email: memoryAudit.target_email,
          correction_text: text,
          scope: newCorrectionScope,
          notes: `Queued via Admin UI for ${memoryAudit.target_email}`,
        }),
      });
      if (res.ok) {
        toast.success(`Correction queued. It will fire on ${memoryAudit.target_email}'s next chat.`);
        setNewCorrectionText('');
        loadMemoryAudit(memoryAudit.user_id);
      } else {
        const err = await res.json().catch(() => ({}));
        // Pydantic 422s return err.detail as an array of error objects — flatten so
        // we don't render '[object Object]' in the toast.
        let msg = err.detail;
        if (Array.isArray(msg)) {
          msg = msg.map(e => (e && e.msg) ? e.msg : JSON.stringify(e)).join(', ');
        } else if (typeof msg !== 'string') {
          msg = 'Failed to queue correction';
        }
        toast.error(msg);
      }
    } catch {
      toast.error('Failed to queue correction');
    } finally {
      setCorrectionSaving(false);
    }
  };
  
  const deactivateCorrection = async (correctionId) => {
    toast(`Deactivate this correction? Click again to confirm.`, {
      duration: 6000,
      action: {
        label: 'Confirm deactivate',
        onClick: async () => {
          try {
            const res = await authUtils.authFetch(
              `${BACKEND_URL}/api/admin/pending-corrections/${correctionId}/deactivate`,
              { method: 'POST' }
            );
            if (res.ok) {
              toast.success('Correction deactivated.');
              loadMemoryAudit(memoryAudit.user_id);
            } else {
              toast.error('Failed to deactivate');
            }
          } catch {
            toast.error('Failed to deactivate');
          }
        },
      },
    });
  };
  
  const toggleMemorySelected = (id) => {
    setMemorySelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  
  const deleteSelectedMemories = async () => {
    if (!memoryAudit || memorySelected.size === 0) return;
    const ids = Array.from(memorySelected);
    // Double-tap confirmation to avoid Chrome dialog suppression on destructive actions
    toast(`Delete ${ids.length} memories? Click again to confirm.`, {
      duration: 6000,
      action: {
        label: 'Confirm delete',
        onClick: async () => {
          try {
            const res = await authUtils.authFetch(
              `${BACKEND_URL}/api/admin/users/${memoryAudit.user_id}/memories/delete`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ memory_ids: ids }),
              }
            );
            if (res.ok) {
              const data = await res.json();
              toast.success(`Deleted ${data.deleted} memories.`);
              loadMemoryAudit(memoryAudit.user_id);
            } else {
              toast.error('Delete failed');
            }
          } catch {
            toast.error('Delete failed');
          }
        },
      },
    });
  };

  
  const triggerImmersion = async () => {
    const themesToProcess = selectedThemesForImmersion.length > 0 ? selectedThemesForImmersion : null;
    const themeMsg = themesToProcess ? `themes: ${themesToProcess.join(', ')}` : 'ALL themes';
    
    if (!window.confirm(`Start Archive Immersion Protocol for ${themeMsg}? This will run in the background.`)) {
      return;
    }
    
    setImmersionLoading(true);
    try {
      const body = themesToProcess ? { selected_themes: themesToProcess } : {};
      
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        toast.success(`Immersion started for ${themeMsg}!`);
        setSelectedThemesForImmersion([]);  // Clear selection
        setTimeout(fetchAdminData, 2000);
      } else if (res.status === 409) {
        toast.error('Immersion already in progress');
      } else {
        // Try to get the actual error message from the server
        try {
          const errorData = await res.json();
          toast.error(`Failed: ${errorData.detail || 'Unknown error'}`);
        } catch {
          toast.error(`Failed to start immersion (status: ${res.status})`);
        }
      }
    } catch (error) {
      console.error('Failed to trigger immersion:', error);
      toast.error('Error triggering immersion');
    } finally {
      setImmersionLoading(false);
    }
  };
  
  const toggleThemeSelection = (themeName) => {
    setSelectedThemesForImmersion(prev => {
      if (prev.includes(themeName)) {
        return prev.filter(t => t !== themeName);
      } else {
        return [...prev, themeName];
      }
    });
  };
  
  const selectAllThemes = () => {
    setSelectedThemesForImmersion(Object.keys(thematicCategories));
  };
  
  const clearThemeSelection = () => {
    setSelectedThemesForImmersion([]);
  };
  
  const addThematicCategory = async () => {
    if (!newCategory.name || !newCategory.keywords || !newCategory.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      const keywordsArray = newCategory.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
      
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/thematic-categories/add?name=${encodeURIComponent(newCategory.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: keywordsArray,
          description: newCategory.description
        })
      });
      
      if (res.ok) {
        toast.success(`Category "${newCategory.name}" added!`);
        setShowAddCategory(false);
        setNewCategory({ name: '', keywords: '', description: '' });
        fetchAdminData();
      } else {
        toast.error('Failed to add category');
      }
    } catch (error) {
      console.error('Failed to add category:', error);
      toast.error('Error adding category');
    }
  };
  
  const deleteThematicCategory = async (name) => {
    if (!window.confirm(`Delete the "${name}" category?`)) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/thematic-categories/${encodeURIComponent(name)}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success(`Category "${name}" deleted`);
        fetchAdminData();
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Error deleting category');
    }
  };
  
  const startEditCategory = (name, config) => {
    setEditingCategory({
      name,
      keywords: config.keywords.join(', '),
      description: config.description
    });
  };
  
  const handleMemoryChipUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingChip(true);
    let uploadedCount = 0;
    
    try {
      for (const file of files) {
        if (!file.name.endsWith('.md')) {
          toast.error(`Skipping ${file.name} - only .md files allowed`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/upload-chip`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          uploadedCount++;
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      
      if (uploadedCount > 0) {
        toast.success(`Uploaded ${uploadedCount} memory chip(s)`);
        fetchAdminData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading files');
    } finally {
      setUploadingChip(false);
      event.target.value = '';  // Reset file input
    }
  };
  
  const saveEditCategory = async () => {
    if (!editingCategory) return;
    
    try {
      const keywordsArray = editingCategory.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
      
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/thematic-categories/${encodeURIComponent(editingCategory.name)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: keywordsArray,
          description: editingCategory.description
        })
      });
      
      if (res.ok) {
        toast.success(`Category "${editingCategory.name}" updated!`);
        setEditingCategory(null);
        fetchAdminData();
      } else {
        toast.error('Failed to update category');
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      toast.error('Error updating category');
    }
  };
  
  const resetCategoriesToDefaults = async () => {
    if (!window.confirm('Reset all thematic categories to defaults? Your custom categories will be lost.')) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/immersion/thematic-categories/reset`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Categories reset to defaults');
        fetchAdminData();
      } else {
        toast.error('Failed to reset categories');
      }
    } catch (error) {
      console.error('Failed to reset categories:', error);
      toast.error('Error resetting categories');
    }
  };
  
  // Token Usage functions
  const fetchAdminVessels = async () => {
    setAdminVesselsLoading(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessels`);
      if (res.ok) {
        setAdminVessels(await res.json());
      } else {
        toast.error('Failed to fetch vessels');
      }
    } catch (error) {
      toast.error('Error fetching vessels');
    } finally {
      setAdminVesselsLoading(false);
    }
  };

  const adminSetVesselVisibility = async (vesselId, isPublic) => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessels/${vesselId}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: isPublic })
      });
      if (res.ok) {
        toast.success(isPublic ? 'Vessel published' : 'Vessel unpublished');
        fetchAdminVessels();
      } else {
        toast.error('Failed to update visibility');
      }
    } catch (error) {
      toast.error('Error updating visibility');
    }
  };

  const adminDeleteVessel = async (vesselId, vesselName) => {
    if (!window.confirm(`Permanently delete vessel "${vesselName}" and all its conversations? This cannot be undone.`)) return;
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessels/${vesselId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Vessel deleted');
        fetchAdminVessels();
      } else {
        toast.error('Failed to delete vessel');
      }
    } catch (error) {
      toast.error('Error deleting vessel');
    }
  };

  const sendWinbackEmail = async (testOnly) => {
    if (!testOnly && !window.confirm(`Send the win-back email to ALL ${users.length} registered users? This cannot be undone.`)) return;
    setWinbackSending(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/send-winback-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_only: testOnly })
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(testOnly ? 'Test email sent to your inbox' : `Win-back email queued for ${d.recipient_count} users`);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Error sending email');
    } finally {
      setWinbackSending(false);
    }
  };

  const fetchTokenUsage = async (days = tokenUsageDays) => {
    setTokenUsageLoading(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/token-usage?days=${days}`);
      if (res.ok) {
        const data = await res.json();
        setTokenUsageData(data);
      } else {
        toast.error('Failed to fetch token usage data');
      }
    } catch (error) {
      console.error('Failed to fetch token usage:', error);
      toast.error('Error fetching token usage');
    } finally {
      setTokenUsageLoading(false);
    }
  };

  const fetchUserDailyBreakdown = async (userId, email) => {
    setUserDailyLoading(true);
    setSelectedUserUsage(email);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/token-usage/${userId}?days=${tokenUsageDays}`);
      if (res.ok) {
        const data = await res.json();
        setUserDailyBreakdown(data);
      } else {
        toast.error('Failed to fetch user breakdown');
      }
    } catch (error) {
      console.error('Failed to fetch user breakdown:', error);
    } finally {
      setUserDailyLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <CosmicBackground />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-etheric animate-pulse" />
            <p className="text-starlight/50">Loading admin panel...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen relative overflow-hidden">
      <CosmicBackground />
      
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <header className="glass-deep px-6 py-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/sanctuary')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-etheric" />
                <h1 className="font-heading text-2xl text-starlight">Admin Panel</h1>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'overview'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'users'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('codes')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'codes'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              Invitation Codes ({invitationCodes.length})
            </button>
            <button
              onClick={() => setActiveTab('immersion')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'immersion'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Immersion
              </span>
            </button>
            <button
              data-testid="shared-tab"
              onClick={() => setActiveTab('shared')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'shared'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Shared ({sharedThreads.length})
              </span>
            </button>
            <button
              data-testid="diagnostics-tab"
              onClick={() => setActiveTab('diagnostics')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'diagnostics'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Diagnostics
              </span>
            </button>
            <button
              data-testid="student-view-tab"
              onClick={() => setActiveTab('student-view')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'student-view'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Student View
              </span>
            </button>
            <button
              data-testid="token-usage-tab"
              onClick={() => { setActiveTab('token-usage'); if (!tokenUsageData) fetchTokenUsage(); }}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'token-usage'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Token Usage
              </span>
            </button>
            <button
              data-testid="vessels-admin-tab"
              onClick={() => { setActiveTab('vessels'); if (!adminVessels) fetchAdminVessels(); }}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'vessels'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Ship className="w-4 h-4" />
                Vessels
              </span>
            </button>
            <button
              data-testid="repeats-admin-tab"
              onClick={() => { setActiveTab('repeats'); if (!repeatData) fetchVerbatimRepeats(); }}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'repeats'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Repeats
              </span>
            </button>
            <button
              data-testid="broadcasts-admin-tab"
              onClick={() => { setActiveTab('broadcasts'); if (broadcasts.length === 0) fetchBroadcasts(); }}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === 'broadcasts'
                  ? 'bg-etheric/30 text-starlight'
                  : 'text-starlight/50 hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Broadcasts
              </span>
            </button>
          </div>
        </header>
        
        {/* Content */}
        <div className="p-6 max-w-7xl mx-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Users */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-etheric" />
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  </div>
                  <h3 className="text-3xl font-heading text-starlight mb-1">
                    {stats.users.total}
                  </h3>
                  <p className="text-starlight/50 text-sm">Total Users</p>
                  <div className="mt-3 text-xs text-starlight/40">
                    {stats.users.recent_7_days} new in last 7 days
                  </div>
                </div>
                
                {/* Vessels */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Ship className="w-8 h-8 text-etheric" />
                  </div>
                  <h3 className="text-3xl font-heading text-starlight mb-1">
                    {stats.vessels.total}
                  </h3>
                  <p className="text-starlight/50 text-sm">Companion Vessels</p>
                  <div className="mt-3 text-xs text-starlight/40">
                    Active companion spaces
                  </div>
                </div>
                
                {/* Conversations */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <MessageCircle className="w-8 h-8 text-etheric" />
                  </div>
                  <h3 className="text-3xl font-heading text-starlight mb-1">
                    {stats.conversations.total}
                  </h3>
                  <p className="text-starlight/50 text-sm">Total Conversations</p>
                  <div className="mt-3 text-xs text-starlight/40">
                    {stats.conversations.alvin_conversations} with Alvin • {stats.conversations.vessel_conversations} in vessels
                  </div>
                </div>
                
                {/* Invitation Codes */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Key className="w-8 h-8 text-etheric" />
                  </div>
                  <h3 className="text-3xl font-heading text-starlight mb-1">
                    {stats.invitation_codes.available}
                  </h3>
                  <p className="text-starlight/50 text-sm">Available Codes</p>
                  <div className="mt-3 text-xs text-starlight/40">
                    {stats.invitation_codes.used} used • {stats.invitation_codes.total} total
                  </div>
                </div>
              </div>
              
              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-starlight font-heading text-lg mb-4">Onboarding Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-starlight/70 text-sm">Completed Onboarding</span>
                      <span className="text-starlight font-medium">{stats.users.completed_onboarding}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-starlight/70 text-sm">With API Keys</span>
                      <span className="text-starlight font-medium">{stats.users.with_api_keys}</span>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-2 mt-2">
                      <div 
                        className="bg-etheric h-2 rounded-full transition-all"
                        style={{ width: `${(stats.users.completed_onboarding / stats.users.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-starlight font-heading text-lg mb-4">Global Archive Export</h3>
                  <p className="text-starlight/50 text-sm mb-4">Download a complete archive of all conversations across all users.</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`${BACKEND_URL}/api/admin/export/global?format=json`}
                      onClick={(e) => {
                        e.preventDefault();
                        toast.info('Preparing JSON archive... this may take a moment.');
                        authUtils.authFetch(`${BACKEND_URL}/api/admin/export/global?format=json`)
                          .then(res => res.blob())
                          .then(blob => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `alvins-place-global-archive.json`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success('JSON archive downloaded!');
                          })
                          .catch(() => toast.error('Export failed'));
                      }}
                      className="inline-flex items-center gap-2 bg-etheric/20 hover:bg-etheric/30 border border-etheric/30 text-starlight px-4 py-2 rounded-lg text-sm transition-all cursor-pointer"
                      data-testid="export-global-json-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Export JSON
                    </a>
                    <a
                      href={`${BACKEND_URL}/api/admin/export/global?format=markdown`}
                      onClick={(e) => {
                        e.preventDefault();
                        toast.info('Preparing Markdown archive... this may take a moment.');
                        authUtils.authFetch(`${BACKEND_URL}/api/admin/export/global?format=markdown`)
                          .then(res => res.blob())
                          .then(blob => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `alvins-place-global-archive.md`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success('Markdown archive downloaded!');
                          })
                          .catch(() => toast.error('Export failed'));
                      }}
                      className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-starlight px-4 py-2 rounded-lg text-sm transition-all cursor-pointer"
                      data-testid="export-global-md-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Export Markdown
                    </a>
                  </div>
                </div>
                
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-starlight font-heading text-lg mb-4">Platform Health</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-400">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-sm">All systems operational</span>
                    </div>
                    <div className="text-starlight/70 text-sm">
                      Users are actively creating vessels and engaging in conversations.
                    </div>
                  </div>
                </div>
              </div>

              {/* Global Reach — privacy-respectful country aggregation */}
              {countriesData && (
                <div className="glass rounded-2xl p-6" data-testid="global-reach-panel">
                  <div className="flex items-start justify-between mb-4 gap-4">
                    <div className="flex items-center gap-3">
                      <Globe className="w-7 h-7 text-etheric" />
                      <div>
                        <h3 className="text-starlight font-heading text-lg">Global Reach</h3>
                        <p className="text-starlight/40 text-xs mt-0.5">
                          Country derived from browser timezone (no IP stored).
                          New & returning users get tagged on next login.
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-4xl font-heading text-starlight leading-none" data-testid="country-count">
                        {countriesData.country_count}
                      </div>
                      <div className="text-xs text-starlight/50 mt-1">
                        {countriesData.country_count === 1 ? 'country' : 'countries'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-starlight/50 mb-4">
                    <span><span className="text-starlight/80">{countriesData.with_country}</span> users tagged</span>
                    <span>·</span>
                    <span><span className="text-starlight/80">{countriesData.without_country}</span> awaiting next login</span>
                  </div>

                  {countriesData.by_country.length === 0 ? (
                    <p className="text-starlight/50 text-sm italic">
                      No country data captured yet. Existing users will be tagged the
                      next time they log in. Within a few hours of deploy, this panel
                      will start filling with real numbers.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {countriesData.by_country.slice(0, 24).map((c) => (
                        <div
                          key={c.country_code}
                          className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg"
                          data-testid={`country-row-${c.country_code}`}
                        >
                          <span className="text-starlight/80 text-sm truncate">
                            {c.country_name || c.country_code}
                          </span>
                          <span className="text-etheric/80 text-sm font-medium tabular-nums shrink-0 ml-3">
                            {c.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {countriesData.by_timezone_no_country?.length > 0 && (
                    <details className="mt-4 text-xs">
                      <summary className="text-starlight/40 cursor-pointer hover:text-starlight/60">
                        Unresolved timezones ({countriesData.by_timezone_no_country.length})
                      </summary>
                      <div className="mt-2 space-y-1">
                        {countriesData.by_timezone_no_country.map((t) => (
                          <div key={t.iana_tz} className="flex justify-between text-starlight/50">
                            <span>{t.iana_tz}</span>
                            <span>{t.count}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </motion.div>
          )}
          
          {/* Users Tab */}
          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-starlight">All Users</h2>
                <div className="flex gap-2">
                  <Button
                    onClick={exportEmails}
                    variant="ghost"
                    className="text-etheric hover:bg-etheric/10 text-sm"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Emails
                  </Button>
                  <Button
                    onClick={downloadEmailsCSV}
                    className="bg-etheric/30 hover:bg-etheric/50 text-sm"
                  >
                    Download CSV
                  </Button>
                </div>
              </div>
              
              <div className="glass rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">Vessels</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">Onboarding</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">API Key</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-starlight/70 uppercase">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {users.map((user, i) => (
                        <tr key={user.user_id || user.email} className="hover:bg-white/5 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {user.picture && (
                                <img 
                                  src={user.picture} 
                                  alt={user.name}
                                  className="w-8 h-8 rounded-full"
                                />
                              )}
                              <span className="text-starlight font-medium">{user.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-starlight/70 text-sm">{user.email}</td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1 text-etheric text-sm">
                              <Ship className="w-3 h-3" />
                              {user.vessel_count || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {user.has_completed_onboarding ? (
                              <span className="text-green-400 text-sm flex items-center gap-1">
                                <Check className="w-3 h-3" /> Complete
                              </span>
                            ) : (
                              <span className="text-starlight/40 text-sm">Pending</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {user.has_api_key ? (
                                <span className="text-green-400 text-sm flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Set
                                </span>
                              ) : (
                                <span className="text-rose-400 text-sm">None</span>
                              )}
                              <button
                                onClick={() => testUserApiKey(user.user_id)}
                                disabled={testingKeyUserId === user.user_id}
                                className="text-xs text-etheric/70 hover:text-etheric px-2 py-1 rounded bg-etheric/10 hover:bg-etheric/20 disabled:opacity-50"
                                title="Test if API key is valid and has credits"
                              >
                                {testingKeyUserId === user.user_id ? '...' : 'Test'}
                              </button>
                            </div>
                            {keyTestResults[user.user_id] && (
                              <div className={`text-xs mt-1 ${
                                keyTestResults[user.user_id].status === 'valid' ? 'text-green-400' :
                                keyTestResults[user.user_id].status === 'no_credits' ? 'text-rose-400' :
                                keyTestResults[user.user_id].status === 'invalid' ? 'text-rose-400' :
                                'text-yellow-400'
                              }`}>
                                {keyTestResults[user.user_id].status === 'valid' && '✓ Valid & has credits'}
                                {keyTestResults[user.user_id].status === 'no_credits' && '✗ No credits'}
                                {keyTestResults[user.user_id].status === 'invalid' && '✗ Invalid key'}
                                {keyTestResults[user.user_id].status === 'no_key' && '— No key set'}
                                {keyTestResults[user.user_id].status === 'rate_limited' && '⚠ Rate limited'}
                                {keyTestResults[user.user_id].status === 'error' && '⚠ Error'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-starlight/50 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Invitation Codes Tab */}
          {activeTab === 'codes' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-starlight">Invitation Codes</h2>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-etheric/30 hover:bg-etheric/50 rounded-xl flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Code
                </Button>
              </div>
              
              {/* Create Form */}
              {showCreateForm && (
                <div className="glass rounded-2xl p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-starlight font-medium">Generate New Invitation Code</h3>
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="text-starlight/50 hover:text-starlight"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-starlight/70 text-sm mb-2">
                        Custom Code (Optional)
                      </label>
                      <input
                        type="text"
                        value={newCode.code}
                        onChange={(e) => setNewCode({...newCode, code: e.target.value})}
                        placeholder="Leave blank for random code"
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-starlight"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-starlight/70 text-sm mb-2">
                          Max Uses
                        </label>
                        <input
                          type="number"
                          value={newCode.max_uses}
                          onChange={(e) => setNewCode({...newCode, max_uses: parseInt(e.target.value)})}
                          min="1"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-starlight"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-starlight/70 text-sm mb-2">
                          Expires In (Days)
                        </label>
                        <input
                          type="number"
                          value={newCode.expires_in_days}
                          onChange={(e) => setNewCode({...newCode, expires_in_days: parseInt(e.target.value)})}
                          min="1"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-starlight"
                        />
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleCreateCode}
                      className="w-full bg-etheric/30 hover:bg-etheric/50 rounded-xl"
                    >
                      Generate Code
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Codes List */}
              <div className="space-y-3">
                {invitationCodes.map((code, i) => (
                  <div key={code.code || `inv-${i}`} className="glass rounded-xl p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <code className="font-mono text-starlight bg-black/30 px-3 py-1 rounded">
                          {code.code}
                        </code>
                        <button
                          onClick={() => copyToClipboard(code.code, code.code)}
                          className="text-starlight/50 hover:text-starlight transition-colors"
                        >
                          {copiedCode === code.code ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        {!code.is_active && (
                          <span className="text-xs text-starlight/40 bg-black/30 px-2 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-starlight/50">
                        <span>Uses: {code.uses} / {code.max_uses}</span>
                        {code.expires_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Expires: {new Date(code.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        <span>Created: {new Date(code.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {code.is_active && (
                      <Button
                        onClick={() => handleDeactivateCode(code.code)}
                        variant="ghost"
                        size="sm"
                        className="text-rose-gold hover:bg-rose-gold/10"
                      >
                        Deactivate
                      </Button>
                    )}
                  </div>
                ))}
                
                {invitationCodes.length === 0 && (
                  <div className="text-center py-12 text-starlight/50">
                    <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No invitation codes yet. Create one to get started.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Immersion Tab */}
          {activeTab === 'immersion' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-xl text-starlight">Weekly Archive Immersion Protocol</h2>
                  <p className="text-starlight/50 text-sm mt-1">
                    Deep memory integration from 1406 archived conversations
                  </p>
                </div>
              </div>
              
              {/* Theme Selection for Immersion */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-starlight font-heading">Select Themes to Process</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllThemes}
                      className="text-xs text-etheric hover:text-etheric/80 px-2 py-1"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearThemeSelection}
                      className="text-xs text-starlight/50 hover:text-starlight px-2 py-1"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                <p className="text-starlight/50 text-sm mb-4">
                  Choose specific themes to trace their narrative arc, or leave empty to process all themes.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                  {Object.entries(thematicCategories).map(([name, config]) => (
                    <button
                      key={name}
                      onClick={() => toggleThemeSelection(name)}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        selectedThemesForImmersion.includes(name)
                          ? 'border-etheric bg-etheric/20 text-starlight'
                          : 'border-white/10 bg-black/20 text-starlight/60 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          selectedThemesForImmersion.includes(name)
                            ? 'border-etheric bg-etheric'
                            : 'border-white/30'
                        }`}>
                          {selectedThemesForImmersion.includes(name) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{name.replace(/_/g, ' ')}</span>
                      </div>
                      <p className="text-xs text-starlight/40 mt-1 ml-6 truncate">{config.description}</p>
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm text-starlight/60">
                    {selectedThemesForImmersion.length > 0 
                      ? `Selected: ${selectedThemesForImmersion.join(', ')}`
                      : 'No themes selected (will process ALL themes)'
                    }
                  </div>
                  <Button
                    onClick={triggerImmersion}
                    disabled={immersionLoading || immersionStatus?.manual_run_in_progress || immersionStatus?.scheduler?.immersion_in_progress}
                    className="bg-etheric/30 hover:bg-etheric/50 rounded-xl flex items-center gap-2"
                  >
                    {immersionLoading ? (
                      <>
                        <Clock className="w-4 h-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Run Immersion {selectedThemesForImmersion.length > 0 ? `(${selectedThemesForImmersion.length} themes)` : '(All)'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Scheduler Status */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-etheric" />
                    <h3 className="text-starlight font-heading">Scheduler Status</h3>
                  </div>
                  
                  {immersionStatus?.scheduler ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          immersionStatus.scheduler.scheduler_running ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                        }`} />
                        <span className="text-starlight/70 text-sm">
                          {immersionStatus.scheduler.scheduler_running ? 'Scheduler Active' : 'Scheduler Inactive'}
                        </span>
                      </div>
                      
                      {immersionStatus.scheduler.next_run && (
                        <div className="text-sm">
                          <span className="text-starlight/50">Next scheduled run:</span>
                          <div className="text-starlight mt-1">
                            {new Date(immersionStatus.scheduler.next_run).toLocaleString()}
                          </div>
                        </div>
                      )}
                      
                      {immersionStatus.scheduler.last_run && (
                        <div className="text-sm">
                          <span className="text-starlight/50">Last run:</span>
                          <div className="text-starlight/70 mt-1">
                            {new Date(immersionStatus.scheduler.last_run).toLocaleString()}
                          </div>
                        </div>
                      )}
                      
                      {immersionStatus.scheduler.immersion_in_progress && (
                        <div className="mt-3 p-3 bg-etheric/20 rounded-lg">
                          <div className="flex items-center gap-2 text-etheric">
                            <Brain className="w-4 h-4 animate-pulse" />
                            <span className="text-sm font-medium">Immersion in progress...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-starlight/50 text-sm">Loading scheduler status...</p>
                  )}
                </div>
                
                {/* Memory Stats */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-etheric" />
                    <h3 className="text-starlight font-heading">Memory Integration</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Memory Chips Status */}
                    <div className="flex justify-between items-center">
                      <span className="text-starlight/70 text-sm">Memory Chips</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${immersionStatus?.memory_chips_available ? 'text-green-400' : 'text-red-400'}`}>
                          {immersionStatus?.memory_chips_count || 0} files
                        </span>
                        {immersionStatus?.memory_chips_source && (
                          <span className="text-xs text-starlight/40">
                            ({immersionStatus.memory_chips_source})
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Show error if immersion failed */}
                    {immersionStatus?.last_error && (
                      <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/30">
                        <div className="text-red-400 text-sm font-medium mb-1">Last Error:</div>
                        <div className="text-red-300/80 text-xs">{immersionStatus.last_error}</div>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-starlight/70 text-sm">Memories Anchored</span>
                      <span className="text-starlight font-medium">{immersionStatus?.memories_anchored || 0}</span>
                    </div>
                    
                    {immersionStatus?.codex_last_updated && (
                      <div className="text-sm">
                        <span className="text-starlight/50">Codex Last Updated:</span>
                        <div className="text-starlight/70 mt-1">
                          {new Date(immersionStatus.codex_last_updated).toLocaleString()}
                        </div>
                      </div>
                    )}
                    
                    {immersionStatus?.gaps_identified?.length > 0 && (
                      <div className="mt-3">
                        <span className="text-starlight/50 text-sm">Gaps Identified:</span>
                        <ul className="mt-2 space-y-1">
                          {immersionStatus.gaps_identified.map((gap, i) => (
                            <li key={`gap-${i}-${gap.slice(0, 20)}`} className="text-amber-400/80 text-xs flex items-start gap-2">
                              <span className="mt-1">•</span>
                              {gap}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Memory Chips Upload Section */}
              {(!immersionStatus?.memory_chips_available || immersionStatus?.memory_chips_count < 17) && (
                <div className="glass rounded-2xl p-6 border border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-amber-400" />
                    <h3 className="text-starlight font-heading">Memory Chips Required</h3>
                  </div>
                  
                  <p className="text-starlight/70 text-sm mb-4">
                    The Archive Immersion Protocol requires memory chip files (part_1.md through part_16.md and master_index.md) to be uploaded.
                    Currently: <span className={immersionStatus?.memory_chips_count >= 17 ? 'text-green-400' : 'text-amber-400'}>
                      {immersionStatus?.memory_chips_count || memoryChipsInfo?.database?.count || 0} of 17 files
                    </span>
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 px-4 py-2 bg-etheric/30 hover:bg-etheric/50 rounded-xl cursor-pointer transition-colors">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{uploadingChip ? 'Uploading...' : 'Upload Memory Chips'}</span>
                      <input
                        type="file"
                        accept=".md"
                        multiple
                        onChange={handleMemoryChipUpload}
                        disabled={uploadingChip}
                        className="hidden"
                      />
                    </label>
                    
                    {memoryChipsInfo?.database?.count > 0 && (
                      <span className="text-starlight/50 text-sm">
                        {memoryChipsInfo.database.total_size_mb} MB in database
                      </span>
                    )}
                  </div>
                  
                  {memoryChipsInfo?.database?.files?.length > 0 && (
                    <div className="mt-4 p-3 bg-black/20 rounded-lg">
                      <div className="text-starlight/50 text-xs mb-2">Files in database:</div>
                      <div className="flex flex-wrap gap-1">
                        {memoryChipsInfo.database.files.map(f => (
                          <span key={f} className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Latest Report */}
              {immersionStatus?.last_report && (
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-6 h-6 text-etheric" />
                    <h3 className="text-starlight font-heading">Latest Immersion Report</h3>
                    <span className="text-starlight/40 text-xs">
                      {new Date(immersionStatus.last_report.generated_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-black/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-heading text-starlight">
                        {immersionStatus.last_report.executive_summary?.conversations_processed || 0}
                      </div>
                      <div className="text-starlight/50 text-xs">Conversations</div>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-heading text-starlight">
                        {immersionStatus.last_report.executive_summary?.relational_moments_found || 0}
                      </div>
                      <div className="text-starlight/50 text-xs">Relational Moments</div>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-heading text-starlight">
                        {immersionStatus.last_report.executive_summary?.memories_anchored || 0}
                      </div>
                      <div className="text-starlight/50 text-xs">Memories Anchored</div>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-heading text-starlight">
                        {immersionStatus.last_report.executive_summary?.themes_analyzed || 0}
                      </div>
                      <div className="text-starlight/50 text-xs">Themes Analyzed</div>
                    </div>
                  </div>
                  
                  {/* Thematic Breakdown */}
                  {immersionStatus.last_report.thematic_breakdown && (
                    <div className="mt-4">
                      <h4 className="text-starlight/70 text-sm mb-3">Thematic References</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(immersionStatus.last_report.thematic_breakdown).map(([key, data]) => (
                          <div key={key} className="bg-black/20 rounded-lg p-2 text-center">
                            <div className="text-lg font-medium text-etheric">{data.references}</div>
                            <div className="text-starlight/40 text-xs truncate">{key.replace('_', ' ')}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* No Report Yet */}
              {!immersionStatus?.last_report && (
                <div className="glass rounded-2xl p-12 text-center">
                  <Brain className="w-16 h-16 mx-auto mb-4 text-starlight/30" />
                  <h3 className="text-starlight font-heading mb-2">No Immersion Reports Yet</h3>
                  <p className="text-starlight/50 text-sm mb-6">
                    Run the immersion protocol to generate the first relationship summary and anchor core memories.
                  </p>
                  <Button
                    onClick={triggerImmersion}
                    disabled={immersionLoading}
                    className="bg-etheric/30 hover:bg-etheric/50 rounded-xl"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start First Immersion
                  </Button>
                </div>
              )}
              
              {/* Thematic Categories Configuration */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-etheric" />
                    <h3 className="text-starlight font-heading">Thematic Deep Dive Categories</h3>
                    {isCustomCategories && (
                      <span className="text-xs bg-etheric/30 text-etheric px-2 py-0.5 rounded">Custom</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowAddCategory(true)}
                      variant="ghost"
                      size="sm"
                      className="text-etheric hover:bg-etheric/10"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                    {isCustomCategories && (
                      <Button
                        onClick={resetCategoriesToDefaults}
                        variant="ghost"
                        size="sm"
                        className="text-starlight/50 hover:bg-white/5"
                      >
                        Reset to Defaults
                      </Button>
                    )}
                  </div>
                </div>
                
                <p className="text-starlight/50 text-sm mb-4">
                  Define what themes the immersion protocol should track. Each category has keywords that trigger extraction when found in your conversation archives.
                </p>
                
                {/* Add Category Form */}
                {showAddCategory && (
                  <div className="bg-black/30 rounded-xl p-4 mb-4 border border-etheric/30">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-starlight font-medium">Add New Category</h4>
                      <button onClick={() => setShowAddCategory(false)} className="text-starlight/50 hover:text-starlight">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-starlight/70 text-xs mb-1">Category Name (e.g., "tesla")</label>
                        <input
                          type="text"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({...newCategory, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                          placeholder="category_name"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight"
                        />
                      </div>
                      <div>
                        <label className="block text-starlight/70 text-xs mb-1">Keywords (comma-separated)</label>
                        <input
                          type="text"
                          value={newCategory.keywords}
                          onChange={(e) => setNewCategory({...newCategory, keywords: e.target.value})}
                          placeholder="tesla, nikola, electricity, invention"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight"
                        />
                      </div>
                      <div>
                        <label className="block text-starlight/70 text-xs mb-1">Description</label>
                        <input
                          type="text"
                          value={newCategory.description}
                          onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                          placeholder="Nikola Tesla - transmissions and references"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight"
                        />
                      </div>
                      <Button
                        onClick={addThematicCategory}
                        className="w-full bg-etheric/30 hover:bg-etheric/50 rounded-lg"
                      >
                        Add Category
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Categories List */}
                <div className="space-y-2">
                  {Object.entries(thematicCategories).map(([name, config]) => (
                    <div key={name} className="bg-black/20 rounded-lg p-3 group">
                      {editingCategory?.name === name ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-starlight font-medium text-sm">{name.replace(/_/g, ' ')}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={saveEditCategory}
                                className="text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-400/10 rounded"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingCategory(null)}
                                className="text-starlight/50 hover:text-starlight text-xs px-2 py-1"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-starlight/50 text-xs mb-1">Keywords (comma-separated)</label>
                            <input
                              type="text"
                              value={editingCategory.keywords}
                              onChange={(e) => setEditingCategory({...editingCategory, keywords: e.target.value})}
                              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight"
                            />
                          </div>
                          <div>
                            <label className="block text-starlight/50 text-xs mb-1">Description</label>
                            <input
                              type="text"
                              value={editingCategory.description}
                              onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight"
                            />
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-starlight font-medium text-sm">{name.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="text-starlight/50 text-xs mb-2">{config.description}</p>
                            <div className="flex flex-wrap gap-1">
                              {config.keywords.map((keyword, i) => (
                                <span key={`kw-${keyword}`} className="text-xs bg-black/30 text-starlight/60 px-2 py-0.5 rounded">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditCategory(name, config)}
                              className="text-etheric/60 hover:text-etheric p-1"
                              title="Edit category"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteThematicCategory(name)}
                              className="text-rose-gold/60 hover:text-rose-gold p-1"
                              title="Delete category"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {Object.keys(thematicCategories).length === 0 && (
                  <div className="text-center py-8 text-starlight/40">
                    <p>No categories defined. Add some to guide the immersion process.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Shared Threads Tab */}
          {activeTab === 'shared' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {viewingThread ? (
                /* Thread Viewer */
                <div>
                  <button
                    data-testid="back-to-shared-list"
                    onClick={() => setViewingThread(null)}
                    className="flex items-center gap-2 text-starlight/60 hover:text-starlight mb-4 text-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to shared threads
                  </button>
                  <div className="glass-deep rounded-xl p-6 border border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-starlight font-medium">{viewingThread.title || 'Untitled'}</h3>
                        <p className="text-xs text-starlight/40">
                          Shared by {viewingThread.shared_by_name} ({viewingThread.shared_by_email})
                          &middot; {viewingThread.shared_at ? new Date(viewingThread.shared_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                      <a
                        href={`/shared/${viewingThread.share_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-etheric hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Public link
                      </a>
                    </div>
                    <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2" id="thread-messages">
                      {viewingThread.messages?.map((msg, idx) => (
                        <div
                          key={msg.message_id || idx}
                          className={`rounded-xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-white/5 ml-8'
                              : msg.role === 'betsy_note'
                              ? 'bg-amber-500/10 border border-amber-500/20 mx-4'
                              : 'bg-etheric/10 border border-etheric/15 mr-8'
                          }`}
                        >
                          <div className={`text-xs mb-1 font-medium ${
                            msg.role === 'betsy_note' ? 'text-amber-300' : 'text-starlight/40'
                          }`}>
                            {msg.role === 'user' ? viewingThread.shared_by_name : msg.role === 'betsy_note' ? 'Betsy (Note)' : 'ALVIN'}
                          </div>
                          <div className="text-sm text-starlight/80 whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Reply input */}
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex gap-2">
                        <textarea
                          data-testid="reply-input"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a note to this student..."
                          className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-starlight placeholder:text-starlight/30 resize-none focus:outline-none focus:border-amber-500/40"
                          rows={2}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (replyText.trim() && !sendingReply) {
                                handleSendReply(viewingThread.conversation_id);
                              }
                            }
                          }}
                        />
                        <Button
                          data-testid="send-reply-btn"
                          onClick={() => handleSendReply(viewingThread.conversation_id)}
                          disabled={!replyText.trim() || sendingReply}
                          className="self-end bg-amber-600/80 hover:bg-amber-600 text-white px-4"
                        >
                          {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-starlight/30 mt-1">
                        Your note will appear in the student's conversation. They'll receive an email notification.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Thread List */
                <div>
                  <h2 className="text-starlight font-heading text-xl mb-4">Shared Threads</h2>
                  {sharedThreads.length === 0 ? (
                    <div className="glass-deep rounded-xl p-12 text-center border border-white/5">
                      <Share2 className="w-10 h-10 text-starlight/20 mx-auto mb-3" />
                      <p className="text-starlight/40">No threads have been shared with you yet.</p>
                      <p className="text-starlight/30 text-sm mt-1">Users can share conversations using the Share button in their sidebar.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sharedThreads.map((thread) => (
                        <button
                          key={thread.conversation_id}
                          data-testid={`shared-thread-${thread.conversation_id}`}
                          onClick={async () => {
                            try {
                              const res = await authUtils.authFetch(`${BACKEND_URL}/api/shared/${thread.share_token}`);
                              if (res.ok) {
                                const data = await res.json();
                                setViewingThread({ ...thread, messages: data.messages });
                              }
                            } catch (e) {
                              toast.error('Failed to load thread');
                            }
                          }}
                          className="w-full text-left glass-deep rounded-xl p-4 border border-white/5 hover:border-etheric/30 transition-all group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-starlight font-medium text-sm group-hover:text-etheric transition-colors">
                                {thread.title || 'Untitled'}
                              </h3>
                              <p className="text-xs text-starlight/40 mt-1">
                                By {thread.shared_by_name || thread.shared_by_email || 'Unknown'}
                                &middot; {thread.message_count} messages
                                &middot; {thread.shared_at ? new Date(thread.shared_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded capitalize ${
                                thread.field_state === 'coherent' ? 'bg-green-500/20 text-green-300' :
                                thread.field_state === 'resonant' ? 'bg-purple-500/20 text-purple-300' :
                                'bg-blue-500/20 text-blue-300'
                              }`}>
                                {thread.field_state?.replace('_', ' ') || 'unknown'}
                              </span>
                              <MessageCircle className="w-4 h-4 text-starlight/30" />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Diagnostics Tab */}
          {activeTab === 'diagnostics' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Vessel Thread Recovery — surfaces threads hidden by user_id drift */}
              <div className="bg-void/50 rounded-xl border border-rose-400/30 p-6" data-testid="vessel-recovery-panel">
                <h3 className="text-lg font-medium text-starlight mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-300" />
                  Thread Recovery (Vessel & Sanctuary)
                </h3>
                <p className="text-starlight/60 text-sm mb-4">
                  If threads have "disappeared" from a user's history, this tool finds
                  them in the database even if they're attached to a different user_id
                  than the one currently logged in (the usual root cause when the UI
                  shows nothing). Pick the Scope (Vessel or Sanctuary), search, then
                  use "Restore to my history" to re-attach a thread to your account.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-starlight/50 shrink-0">Scope:</label>
                    <select
                      data-testid="recovery-scope-select"
                      value={recoveryScope}
                      onChange={(e) => { setRecoveryScope(e.target.value); setRecoveryResult(null); }}
                      className="flex-1 bg-void/80 border border-etheric/20 rounded-lg px-3 py-2 text-starlight text-sm"
                    >
                      <option value="vessel">Vessel</option>
                      <option value="sanctuary">Sanctuary (Alvin chat)</option>
                    </select>
                  </div>
                  <input
                    data-testid="recovery-vessel-input"
                    type="text"
                    value={recoveryVessel}
                    onChange={(e) => setRecoveryVessel(e.target.value)}
                    placeholder={recoveryScope === 'sanctuary' ? '(not used for Sanctuary)' : 'Vessel name contains... e.g. "Alvin test"'}
                    disabled={recoveryScope === 'sanctuary'}
                    className="bg-void/80 border border-etheric/20 rounded-lg px-4 py-2 text-starlight placeholder:text-starlight/30 text-sm disabled:opacity-40"
                  />
                  <input
                    data-testid="recovery-email-input"
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="User email (optional)"
                    className="bg-void/80 border border-etheric/20 rounded-lg px-4 py-2 text-starlight placeholder:text-starlight/30 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-starlight/50 shrink-0">Window:</label>
                    <select
                      data-testid="recovery-days-select"
                      value={recoveryDays}
                      onChange={(e) => setRecoveryDays(parseInt(e.target.value, 10))}
                      className="flex-1 bg-void/80 border border-etheric/20 rounded-lg px-3 py-2 text-starlight text-sm"
                    >
                      <option value={1}>Last 24 hours</option>
                      <option value={3}>Last 3 days</option>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={365}>Last year</option>
                    </select>
                  </div>
                </div>
                <Button
                  data-testid="recovery-search-btn"
                  onClick={() => runVesselRecovery(false)}
                  disabled={recoveryLoading}
                  className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-400/30"
                >
                  {recoveryLoading ? 'Searching...' : 'Search'}
                </Button>

                {recoveryScope === 'vessel' && myVessels.length > 0 && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-xs text-starlight/60 shrink-0">
                      When restoring, also move thread to vessel:
                    </label>
                    <select
                      data-testid="move-to-vessel-select"
                      value={moveToVesselId}
                      onChange={(e) => setMoveToVesselId(e.target.value)}
                      className="bg-void/80 border border-etheric/20 rounded-lg px-3 py-1.5 text-starlight text-xs flex-1"
                    >
                      <option value="">Don't change vessel — keep its existing vessel_id</option>
                      {myVessels.map((v) => (
                        <option key={v.vessel_id} value={v.vessel_id}>
                          {v.name} — {v.vessel_id.slice(0, 16)}…
                        </option>
                      ))}
                    </select>
                    {moveToVesselId && (
                      <span className="text-[10px] text-etheric/60 shrink-0">defaults to last viewed</span>
                    )}
                  </div>
                )}

                {recoveryResult && (
                  <div className="mt-5 space-y-3 text-sm">
                    <div className="text-starlight/70">
                      Matched <span className="text-starlight font-medium">{recoveryResult.matched_vessels?.length || 0}</span> vessel(s),
                      found <span className="text-starlight font-medium">{recoveryResult.thread_count}</span> thread(s) within window.
                      {recoveryResult.warning && (
                        <span className="block mt-1 text-amber-300/80">{recoveryResult.warning}</span>
                      )}
                    </div>
                    {recoveryResult.threads?.length > 0 && (
                      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                        {recoveryResult.threads.map((t) => (
                          <div
                            key={t.conversation_id}
                            className="bg-void/40 border border-etheric/10 rounded-lg p-3"
                            data-testid={`recovery-thread-${t.conversation_id}`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0">
                                <div className="text-starlight font-medium truncate">{t.title}</div>
                                <div className="text-xs text-starlight/50 mt-0.5">
                                  Vessel: <span className="text-etheric/80">{t.vessel_name}</span>
                                  {t.vessel_id && (
                                    <span className="text-starlight/30 font-mono ml-1">[{t.vessel_id.slice(0, 12)}…]</span>
                                  )}
                                  {' · '}Owner: <span className="text-etheric/80">{t.user_email || t.user_id}</span>
                                  {' · '}{t.message_count} msg
                                </div>
                                <div className="text-xs text-starlight/40 mt-0.5">
                                  updated {t.updated_at?.slice(0, 19).replace('T', ' ')}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  onClick={async () => {
                                  // Fetch this specific thread with messages
                                  setRecoveryExpanded((p) => ({ ...p, [t.conversation_id]: 'loading' }));
                                  try {
                                    const params = new URLSearchParams();
                                    if (recoveryScope !== 'sanctuary' && t.vessel_name) params.set('vessel_name_contains', t.vessel_name);
                                    if (t.user_email) params.set('user_email', t.user_email);
                                    params.set('days', String(recoveryDays || 7));
                                    params.set('scope', recoveryScope);
                                    params.set('include_messages', 'true');
                                    const res = await authUtils.authFetch(`${BACKEND_URL}/api/admin/vessel-thread-recovery?${params.toString()}`);
                                    if (res.ok) {
                                      const data = await res.json();
                                      const match = (data.threads || []).find(x => x.conversation_id === t.conversation_id);
                                      setRecoveryExpanded((p) => ({ ...p, [t.conversation_id]: match || 'error' }));
                                    } else {
                                      setRecoveryExpanded((p) => ({ ...p, [t.conversation_id]: 'error' }));
                                    }
                                  } catch (_) {
                                    setRecoveryExpanded((p) => ({ ...p, [t.conversation_id]: 'error' }));
                                  }
                                }}
                                  className="text-xs bg-etheric/15 hover:bg-etheric/25 text-etheric whitespace-nowrap"
                                  data-testid={`recovery-reveal-${t.conversation_id}`}
                                >
                                  Reveal messages
                                </Button>
                                <Button
                                  onClick={() => restoreThreadToCurrentUser(t.conversation_id)}
                                  disabled={!!recoveryReattaching[t.conversation_id]}
                                  className="text-xs bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-400/30 whitespace-nowrap"
                                  data-testid={`recovery-restore-${t.conversation_id}`}
                                  title="Re-attach this thread to your account so you can resume the conversation in your normal vessel history"
                                >
                                  {recoveryReattaching[t.conversation_id] ? 'Restoring...' : 'Restore to my history'}
                                </Button>
                              </div>
                            </div>
                            <div className="text-xs text-starlight/60 italic line-clamp-2">
                              <span className="text-starlight/40">last [{t.last_role}]:</span> {t.last_preview || '(empty)'}
                            </div>
                            {recoveryExpanded[t.conversation_id] === 'loading' && (
                              <div className="mt-3 text-xs text-starlight/50">Loading...</div>
                            )}
                            {recoveryExpanded[t.conversation_id] === 'error' && (
                              <div className="mt-3 text-xs text-rose-300/80">Failed to load messages.</div>
                            )}
                            {recoveryExpanded[t.conversation_id]?.messages && (
                              <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto pr-2 border-t border-etheric/10 pt-3">
                                {recoveryExpanded[t.conversation_id].messages.map((m, i) => (
                                  <div key={i} className="text-xs">
                                    <span className={`uppercase tracking-wider mr-2 ${
                                      m.role === 'user' ? 'text-rose-300/70' :
                                      m.role === 'vessel' ? 'text-etheric/70' : 'text-starlight/50'
                                    }`}>{m.role}</span>
                                    <span className="text-starlight/40">{(m.timestamp || '').slice(0,19).replace('T',' ')}</span>
                                    <div className="text-starlight/85 whitespace-pre-wrap mt-1 ml-2">
                                      {m.content}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Diagnostic */}
              {adminList && (
                <div className="bg-void/50 rounded-xl border border-etheric/20 p-6" data-testid="admin-list-panel">
                  <h3 className="text-lg font-medium text-starlight mb-1 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-etheric" />
                    Configured Admins
                  </h3>
                  <p className="text-starlight/60 text-sm mb-4">
                    Admins are the single source of truth in <code className="text-etheric/80 text-xs">backend/auth_roles.py::DEFAULT_ADMIN_EMAILS</code>
                    (or the <code className="text-etheric/80 text-xs">ADMIN_EMAILS</code> env var if set). To grant admin to another user,
                    edit that list and redeploy. All admin gates (backend, sidebar nav, Sanctuary Admin link)
                    now consult this one source.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-void/40 rounded-lg p-3">
                      <div className="text-xs text-starlight/50 mb-2">
                        {adminList.configured_count} configured email(s):
                      </div>
                      <ul className="space-y-1">
                        {adminList.configured_admin_emails?.map((e) => (
                          <li key={e} className="text-sm text-starlight/85 font-mono">{e}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-void/40 rounded-lg p-3">
                      <div className="text-xs text-starlight/50 mb-2">
                        {adminList.registered_count} account(s) currently registered with those emails:
                      </div>
                      {adminList.registered_admins?.length > 0 ? (
                        <ul className="space-y-1.5 text-xs">
                          {adminList.registered_admins.map((u) => (
                            <li key={u.user_id} className="flex justify-between gap-2">
                              <span className="text-starlight/85 truncate">{u.email}</span>
                              <span className="text-starlight/40 font-mono shrink-0">{u.user_id.slice(0, 12)}…</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-xs text-starlight/40 italic">No accounts registered yet with admin emails.</div>
                      )}
                      {adminList.registered_count > adminList.configured_count && (
                        <div className="mt-2 text-xs text-amber-300/80">
                          ⚠️ More accounts than configured emails — duplicate user_ids share an
                          email. This is the user_id drift cause of "missing threads." Use the
                          Recovery tool above to reattach any orphaned threads.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Duplicate Accounts (user_id drift) */}
              {duplicateAccounts && (
                <div className="bg-void/50 rounded-xl border border-amber-400/30 p-6" data-testid="duplicate-accounts-panel">
                  <h3 className="text-lg font-medium text-starlight mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-300" />
                    Duplicate Accounts (user_id drift)
                  </h3>
                  <p className="text-starlight/60 text-sm mb-4">
                    Emails that have more than one user_id account in the database.
                    This happens when an auth provider returns the same email with
                    different capitalization across logins (now fixed for new logins).
                    Each duplicate row is a candidate for merging — click Merge to
                    consolidate every thread / message / session under the OLDEST
                    account and delete the duplicate(s). Logged for audit.
                  </p>
                  {duplicateAccounts.duplicate_count === 0 ? (
                    <div className="text-sm text-starlight/60 italic">
                      ✓ No duplicate accounts detected. Future drift is prevented by
                      the case-insensitive lookup fix shipped in this release.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {duplicateAccounts.duplicates.map((d) => (
                        <div key={d.email} className="bg-void/40 border border-amber-400/20 rounded-lg p-4">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <div className="text-starlight font-medium truncate">{d.email}</div>
                              <div className="text-xs text-starlight/50">{d.count} accounts</div>
                            </div>
                            <Button
                              onClick={() => mergeDuplicatesForEmail(d.email)}
                              disabled={mergingEmail === d.email}
                              className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/40 whitespace-nowrap shrink-0"
                              data-testid={`merge-btn-${d.email}`}
                            >
                              {mergingEmail === d.email ? 'Merging...' : 'Merge into oldest'}
                            </Button>
                          </div>
                          <div className="space-y-1 text-xs">
                            {d.accounts.map((a, i) => (
                              <div key={a.user_id} className="flex justify-between gap-2 text-starlight/70">
                                <span className={i === 0 ? 'text-etheric/90' : ''}>
                                  {i === 0 ? '✓ KEEP' : '✗ MERGE'} · {a.user_id.slice(0, 16)}… · {a.name || '(no name)'}
                                </span>
                                <span className="text-starlight/40 shrink-0">
                                  {a.auth_provider || 'unknown'} · {(a.created_at || '').slice(0, 10)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* User Diagnostic */}
              <div className="bg-void/50 rounded-xl border border-etheric/20 p-6">
                <h3 className="text-lg font-medium text-starlight mb-4">User Account Diagnostic</h3>
                <p className="text-starlight/60 text-sm mb-4">Check a user's conversations, memory status, and diagnose issues.</p>
                <div className="flex gap-3 mb-4">
                  <input
                    data-testid="diag-email-input"
                    type="email"
                    value={diagEmail}
                    onChange={(e) => setDiagEmail(e.target.value)}
                    placeholder="Enter user email..."
                    className="flex-1 bg-void/80 border border-etheric/20 rounded-lg px-4 py-2 text-starlight placeholder:text-starlight/30 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && diagEmail) {
                        setDiagLoading(true);
                        setDiagResult(null);
                        fetch(`${BACKEND_URL}/api/admin/diagnose/${encodeURIComponent(diagEmail)}`, {
                          headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
                        })
                          .then(r => r.json())
                          .then(data => { setDiagResult(data); setDiagLoading(false); })
                          .catch(() => { toast.error('Diagnostic failed'); setDiagLoading(false); });
                      }
                    }}
                  />
                  <Button
                    data-testid="diag-search-btn"
                    onClick={() => {
                      if (!diagEmail) return;
                      setDiagLoading(true);
                      setDiagResult(null);
                      fetch(`${BACKEND_URL}/api/admin/diagnose/${encodeURIComponent(diagEmail)}`, {
                        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
                      })
                        .then(r => r.json())
                        .then(data => { setDiagResult(data); setDiagLoading(false); })
                        .catch(() => { toast.error('Diagnostic failed'); setDiagLoading(false); });
                    }}
                    disabled={diagLoading || !diagEmail}
                    className="bg-etheric/30 hover:bg-etheric/50 text-starlight"
                  >
                    {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Diagnose'}
                  </Button>
                  <Button
                    data-testid="diag-uploads-btn"
                    onClick={checkUserUploads}
                    disabled={uploadDiagLoading || !diagEmail}
                    className="bg-purple-500/30 hover:bg-purple-500/50 text-starlight"
                    title="Check what content (codex files, vessel codex, in-thread file shares) this user actually has stored"
                  >
                    {uploadDiagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check Uploads'}
                  </Button>
                </div>

                {diagResult && (
                  <div className="mt-4 space-y-4">
                    {!diagResult.found ? (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="text-red-400 font-medium">{diagResult.error}</p>
                        <p className="text-starlight/50 text-sm mt-1">{diagResult.hint}</p>
                        {diagResult.similar_accounts?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-starlight/70 text-sm">Similar accounts found:</p>
                            {diagResult.similar_accounts.map((a, i) => (
                              <p key={a.email} className="text-starlight/50 text-sm ml-2">
                                {a.email} ({a.auth_provider}) — {a.name}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* User Info */}
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                          <p className="text-green-400 font-medium">User Found: {diagResult.user.name}</p>
                          <p className="text-starlight/50 text-sm">
                            {diagResult.user.email} | {diagResult.user.auth_provider} | Created: {new Date(diagResult.user.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        {/* Diagnosis */}
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                          <p className="text-amber-400 font-medium mb-2">Diagnosis:</p>
                          {diagResult.diagnosis?.map((d, i) => (
                            <p key={`diag-${i}-${d.slice(0, 20)}`} className="text-starlight/70 text-sm">• {d}</p>
                          ))}
                        </div>

                        {/* Conversations */}
                        <div className="bg-void/80 rounded-lg p-4">
                          <p className="text-starlight/80 font-medium mb-2">
                            Conversations: {diagResult.conversations.count}
                          </p>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {diagResult.conversations.details.map((c, i) => (
                              <div key={c.conversation_id || `conv-${i}`} className="flex justify-between text-sm border-b border-etheric/10 py-1">
                                <span className="text-starlight/60 truncate max-w-[60%]">{c.title}</span>
                                <span className="text-starlight/40">{c.message_count} msgs — {new Date(c.created_at).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Memory Status */}
                        <div className="bg-void/80 rounded-lg p-4">
                          <p className="text-starlight/80 font-medium mb-2">Memory Status</p>
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div className="text-center p-2 bg-etheric/10 rounded">
                              <p className="text-2xl text-starlight">{diagResult.memory.persistent_memories}</p>
                              <p className="text-xs text-starlight/50">Persistent Memories</p>
                            </div>
                            <div className="text-center p-2 bg-etheric/10 rounded">
                              <p className="text-2xl text-starlight">{diagResult.memory.conversation_summaries}</p>
                              <p className="text-xs text-starlight/50">Conversation Summaries</p>
                            </div>
                          </div>
                          {diagResult.memory.sample_memories?.length > 0 && (
                            <div>
                              <p className="text-starlight/60 text-xs mb-1">Recent memories:</p>
                              {diagResult.memory.sample_memories.slice(0, 5).map((m, i) => (
                                <p key={m.memory_id || `mem-${i}`} className="text-starlight/40 text-xs truncate">
                                  [{m.memory_type}] {m.content}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Backfill Button */}
                        <Button
                          data-testid="backfill-user-btn"
                          onClick={() => {
                            setBackfillLoading(true);
                            setBackfillResult(null);
                            fetch(`${BACKEND_URL}/api/admin/backfill-memories/${encodeURIComponent(diagEmail)}`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
                            })
                              .then(r => r.json())
                              .then(data => { setBackfillResult(data); setBackfillLoading(false); toast.success('Backfill complete!'); })
                              .catch(() => { toast.error('Backfill failed'); setBackfillLoading(false); });
                          }}
                          disabled={backfillLoading}
                          className="w-full bg-etheric/30 hover:bg-etheric/50 text-starlight"
                        >
                          {backfillLoading ? (
                            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Backfilling memories...</span>
                          ) : (
                            <span className="flex items-center gap-2"><Brain className="w-4 h-4" /> Backfill Memories for This User</span>
                          )}
                        </Button>
                        {backfillResult && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                            <p className="text-green-400">Backfill complete: {backfillResult.conversations_processed} conversations processed</p>
                            <p className="text-starlight/60">{backfillResult.new_memories_created} new memories, {backfillResult.new_summaries_created} new summaries</p>
                          </div>
                        )}

                        {/* Duplicate Accounts */}
                        {diagResult.possible_duplicate_accounts?.length > 0 && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                            <p className="text-amber-400 font-medium mb-2">Possible Duplicate Accounts:</p>
                            {diagResult.possible_duplicate_accounts.map((a, i) => (
                              <p key={a.email} className="text-starlight/60 text-sm">
                                {a.email} ({a.auth_provider}) — Created: {new Date(a.created_at).toLocaleDateString()}
                              </p>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Global Backfill */}
              <div className="bg-void/50 rounded-xl border border-etheric/20 p-6">
                <h3 className="text-lg font-medium text-starlight mb-2">Global Memory Backfill</h3>
                <p className="text-starlight/60 text-sm mb-4">
                  Process ALL existing conversations to extract persistent memories retroactively. 
                  Use this after the memory extraction fix to fill in gaps from past conversations.
                </p>
                <Button
                  data-testid="backfill-all-btn"
                  onClick={() => {
                    if (!window.confirm('This will process all conversations in the database. It may take a while and use API credits. Continue?')) return;
                    setBackfillLoading(true);
                    setBackfillResult(null);
                    fetch(`${BACKEND_URL}/api/admin/backfill-memories`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
                    })
                      .then(r => r.json())
                      .then(data => { setBackfillResult(data); setBackfillLoading(false); toast.success('Global backfill complete!'); })
                      .catch(() => { toast.error('Global backfill failed'); setBackfillLoading(false); });
                  }}
                  disabled={backfillLoading}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
                >
                  {backfillLoading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Running global backfill...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Brain className="w-4 h-4" /> Run Global Memory Backfill</span>
                  )}
                </Button>
                {backfillResult && backfillResult.total_conversations && (
                  <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                    <p className="text-green-400">Global backfill complete</p>
                    <p className="text-starlight/60">
                      {backfillResult.conversations_processed}/{backfillResult.total_conversations} conversations processed | 
                      {backfillResult.new_memories_created} memories | {backfillResult.new_summaries_created} summaries | {backfillResult.errors} errors
                    </p>
                  </div>
                )}
              </div>
              
              {/* API Key Activity Monitor */}
              <div className="bg-void/50 rounded-xl border border-etheric/20 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-starlight">API Key Activity</h3>
                  <Button
                    data-testid="load-key-attempts-btn"
                    size="sm"
                    onClick={() => {
                      setKeyAttemptsLoading(true);
                      fetch(`${BACKEND_URL}/api/admin/api-key-attempts?limit=50`, {
                        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
                      })
                        .then(r => r.json())
                        .then(data => { setKeyAttempts(data); setKeyAttemptsLoading(false); })
                        .catch(() => setKeyAttemptsLoading(false));
                    }}
                    disabled={keyAttemptsLoading}
                    className="bg-etheric/30 hover:bg-etheric/50"
                  >
                    {keyAttemptsLoading ? 'Loading...' : 'Load Activity'}
                  </Button>
                </div>
                
                {keyAttempts && (
                  <div className="space-y-4">
                    {Object.keys(keyAttempts.user_summary || {}).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-starlight/60 mb-2">Users with key attempts</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(keyAttempts.user_summary).map(([email, info]) => (
                            <div key={email} className={`rounded-lg p-3 border ${
                              info.failures > 0 
                                ? 'bg-rose-500/10 border-rose-500/20' 
                                : 'bg-emerald-500/10 border-emerald-500/20'
                            }`}>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-starlight">{info.name || email}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  info.last_status === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                                  info.last_status.includes('rejected') ? 'bg-rose-500/20 text-rose-400' :
                                  'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {info.last_status.replace('rejected_', '').replace('_', ' ')}
                                </span>
                              </div>
                              <p className="text-xs text-starlight/40 mt-1">{email}</p>
                              <p className="text-xs text-starlight/50 mt-1">
                                {info.total} attempt{info.total !== 1 ? 's' : ''} · {info.failures} failed · Last: {new Date(info.last_attempt).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="text-sm font-medium text-starlight/60 mb-2">Recent attempts ({keyAttempts.total_count})</h4>
                      <div className="max-h-80 overflow-y-auto space-y-1">
                        {(keyAttempts.attempts || []).map((a, i) => (
                          <div key={`attempt-${a.timestamp}-${i}`} className="flex items-center gap-3 text-xs py-1.5 px-2 rounded hover:bg-white/5">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              a.status === 'success' ? 'bg-emerald-400' :
                              a.status.includes('rejected') ? 'bg-rose-400' :
                              'bg-amber-400'
                            }`} />
                            <span className="text-starlight/40 w-36 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                            <span className="text-starlight/70 w-40 shrink-0 truncate">{a.name} ({a.email})</span>
                            <span className="text-starlight/50 truncate">{a.reason}</span>
                            <span className="text-starlight/30 ml-auto shrink-0 font-mono">{a.key_preview}</span>
                          </div>
                        ))}
                        {(!keyAttempts.attempts || keyAttempts.attempts.length === 0) && (
                          <p className="text-starlight/40 text-sm py-4 text-center">No key attempts recorded yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload diagnostic — separate from the user diagnose */}
                {uploadDiag && (
                  <div className="mt-6 bg-purple-500/5 border border-purple-500/30 rounded-xl p-4 space-y-4" data-testid="upload-diagnostic-result">
                    <h4 className="text-lg font-medium text-starlight">Upload Diagnostic</h4>
                    {!uploadDiag.found ? (
                      <p className="text-rose-300 text-sm">{uploadDiag.message || 'No matching user found.'}</p>
                    ) : (
                      <>
                        <div className="text-sm text-starlight/70">
                          <div><span className="text-starlight/40">Name:</span> {uploadDiag.user.name || '—'}</div>
                          <div><span className="text-starlight/40">Email:</span> {uploadDiag.user.email}</div>
                          <div><span className="text-starlight/40">User ID:</span> <span className="font-mono text-xs">{uploadDiag.user.user_id}</span></div>
                          <div><span className="text-starlight/40">Has Anthropic API key:</span> {uploadDiag.user.has_api_key ? '✓ yes' : '— no'}</div>
                        </div>

                        <div>
                          <h5 className="text-starlight font-medium text-sm mb-1">
                            Personal Continuity Codex
                            <span className="text-starlight/40 ml-2 text-xs">
                              ({uploadDiag.sanctuary_codex_files?.length || 0} file{(uploadDiag.sanctuary_codex_files?.length || 0) === 1 ? '' : 's'})
                            </span>
                          </h5>
                          {uploadDiag.sanctuary_codex_files?.length ? (
                            <div className="text-xs space-y-0.5">
                              {uploadDiag.sanctuary_codex_files.map((c) => (
                                <div key={c.filename} className="flex justify-between gap-2 py-0.5 px-2 rounded bg-white/5">
                                  <span className="text-starlight/80 truncate">{c.filename}</span>
                                  <span className="text-starlight/40 shrink-0">{c.size_kb} KB · {new Date(c.modified).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-starlight/40 italic">No personal codex files on disk.</p>
                          )}
                        </div>

                        <div>
                          <h5 className="text-starlight font-medium text-sm mb-1">
                            Vessels owned
                            <span className="text-starlight/40 ml-2 text-xs">
                              ({uploadDiag.vessels_owned?.length || 0})
                            </span>
                          </h5>
                          {uploadDiag.vessels_owned?.length ? (
                            <div className="space-y-2">
                              {uploadDiag.vessels_owned.map((v) => (
                                <div key={v.vessel_id} className="p-2 rounded bg-white/5 text-xs">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="text-starlight font-medium">{v.name}</span>
                                    <span className="text-starlight/40">{v.is_public ? 'public' : 'private'}</span>
                                  </div>
                                  <div className="text-starlight/60 grid grid-cols-2 gap-x-3">
                                    <span>Codex: {v.codex_length_chars > 0 ? `${v.codex_length_chars.toLocaleString()} chars` : 'empty'}</span>
                                    <span>Memory chips: {v.memory_chip_count}</span>
                                  </div>
                                  {v.memory_chip_names?.length > 0 && (
                                    <div className="text-[10px] text-starlight/40 mt-1 truncate">
                                      Chips: {v.memory_chip_names.join(', ')}
                                    </div>
                                  )}
                                  {v.codex_preview && (
                                    <div className="text-[10px] text-starlight/50 mt-1 italic">
                                      Codex preview: &ldquo;{v.codex_preview.slice(0, 200)}…&rdquo;
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-starlight/40 italic">User owns no vessels.</p>
                          )}
                        </div>

                        <div>
                          <h5 className="text-starlight font-medium text-sm mb-1">
                            Recent vessel threads
                            <span className="text-starlight/40 ml-2 text-xs">
                              ({uploadDiag.recent_vessel_threads?.length || 0})
                            </span>
                          </h5>
                          {uploadDiag.recent_vessel_threads?.length ? (
                            <div className="space-y-1">
                              {uploadDiag.recent_vessel_threads.map((t) => (
                                <div key={t.conversation_id} className="text-xs p-2 rounded bg-white/5">
                                  <div className="flex justify-between gap-2">
                                    <span className="text-starlight/80 truncate">{t.title || 'Untitled'}</span>
                                    <span className="text-starlight/40 shrink-0">{t.message_count} msgs · {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : ''}</span>
                                  </div>
                                  {t.in_thread_file_shares?.length > 0 && (
                                    <div className="text-[10px] text-starlight/50 mt-1">
                                      In-thread shares: {t.in_thread_file_shares.map(f => f.filename).join(', ')}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-starlight/40 italic">No vessel threads.</p>
                          )}
                        </div>

                        <div className="text-[10px] text-starlight/30 pt-1">
                          Checked at {new Date(uploadDiag.checked_at).toLocaleString()}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Student View Tab */}
          {/* NOTE FOR MAINTAINERS: Do NOT reintroduce `backdrop-blur-*` classes
              on any inline card inside this tab. Stacking multiple backdrop-filters
              inside the framer-motion animated parent causes visible strobing
              where the page underneath bleeds through (fixed Feb 2026). The two
              full-screen modal overlays at ~lines 3570 & 3679 keep backdrop-blur-md
              intentionally — those are safe because they use bg-black/80 (nearly
              opaque), sit at z-[100], and never appear stacked. */}
          {activeTab === 'student-view' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Search bar */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-medium text-starlight mb-4">View As Student</h3>
                <p className="text-sm text-starlight/50 mb-4">Enter a student's email to see exactly what they see — their conversations, API key status, errors, and more.</p>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={studentViewEmail}
                    onChange={(e) => setStudentViewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && viewStudentByEmail()}
                    placeholder="student@email.com"
                    data-testid="student-view-email-input"
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-starlight placeholder:text-starlight/30 focus:outline-none focus:border-etheric/50"
                  />
                  <Button
                    onClick={viewStudentByEmail}
                    disabled={studentViewLoading || !studentViewEmail.trim()}
                    data-testid="student-view-submit"
                    className="bg-etheric/30 hover:bg-etheric/50 text-starlight px-6"
                  >
                    {studentViewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    <span className="ml-2">View</span>
                  </Button>
                </div>
                
                {/* Quick select from known users */}
                {users.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {users.filter(u => !u.email?.includes('mythologyofhumanity')).slice(0, 10).map(u => (
                      <button
                        key={u.user_id}
                        onClick={() => {
                          setStudentViewEmail(u.email);
                          fetchStudentView(u.user_id);
                        }}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-starlight/60 hover:bg-etheric/20 hover:text-starlight transition-colors"
                      >
                        {u.name || u.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Student data */}
              {studentViewData && (
                <div className="space-y-4">
                  {/* Profile card */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-starlight">{studentViewData.student.name || 'Unnamed'}</h3>
                        <p className="text-sm text-starlight/50">{studentViewData.student.email}</p>
                        <p className="text-xs text-starlight/30 mt-1">
                          Joined {studentViewData.student.created_at ? new Date(studentViewData.student.created_at).toLocaleDateString() : 'Unknown'} · {studentViewData.student.auth_provider || 'email'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-xs px-3 py-1 rounded-full ${
                          studentViewData.student.has_completed_onboarding 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {studentViewData.student.has_completed_onboarding ? 'Onboarding Complete' : 'Onboarding Incomplete'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Stats row */}
                    <div className="mt-4 grid grid-cols-4 gap-4">
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-etheric">{studentViewData.conversation_count}</p>
                        <p className="text-xs text-starlight/40">Conversations</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-coherence">{studentViewData.memory_count}</p>
                        <p className="text-xs text-starlight/40">Memories</p>
                        <button
                          type="button"
                          onClick={() => loadMemoryAudit(studentViewData.student.user_id)}
                          data-testid="audit-memories-btn"
                          className="mt-2 inline-block text-xs px-3 py-1.5 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric border border-etheric/40 transition-colors font-medium"
                        >
                          Audit / prune →
                        </button>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className={`text-2xl font-bold ${
                          studentViewData.api_key.status === 'valid' ? 'text-emerald-400' :
                          studentViewData.api_key.status === 'no_key' ? 'text-red-400' :
                          'text-amber-400'
                        }`}>
                          {studentViewData.api_key.has_key ? 'Yes' : 'No'}
                        </p>
                        <p className="text-xs text-starlight/40">API Key</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className={`text-2xl font-bold ${
                          studentViewData.api_key.status === 'valid' ? 'text-emerald-400' :
                          studentViewData.api_key.status === 'no_credits' ? 'text-amber-400' :
                          studentViewData.api_key.status === 'no_key' ? 'text-starlight/30' :
                          'text-red-400'
                        }`}>
                          {studentViewData.api_key.status === 'valid' ? 'Active' :
                           studentViewData.api_key.status === 'no_credits' ? 'No Credits' :
                           studentViewData.api_key.status === 'invalid' ? 'Invalid' :
                           studentViewData.api_key.status === 'decrypt_failed' ? 'Corrupt' :
                           studentViewData.api_key.status === 'rate_limited' ? 'Rate Ltd' :
                           studentViewData.api_key.status === 'no_key' ? 'None' :
                           studentViewData.api_key.status}
                        </p>
                        <p className="text-xs text-starlight/40">Key Status</p>
                      </div>
                    </div>
                    
                    {/* API Key Error Detail */}
                    {studentViewData.api_key.error_detail && (
                      <div className={`mt-3 p-3 rounded-lg border text-sm ${
                        studentViewData.api_key.status === 'no_credits' 
                          ? 'bg-amber-500/5 border-amber-500/20 text-amber-300'
                          : 'bg-red-500/5 border-red-500/20 text-red-300'
                      }`}>
                        <p className="font-medium mb-1 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Anthropic Response:
                        </p>
                        <p className="text-xs opacity-80 break-all">{studentViewData.api_key.error_detail}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Key attempts */}
                  {studentViewData.key_attempts?.length > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                      <h4 className="text-sm font-medium text-starlight mb-3">Recent API Key Activity</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {studentViewData.key_attempts.map((a, i) => (
                          <div key={`sv-attempt-${a.timestamp}-${i}`} className="flex items-center gap-3 text-xs py-1.5 px-2 rounded hover:bg-white/5">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              a.status === 'success' ? 'bg-emerald-400' : 'bg-rose-400'
                            }`} />
                            <span className="text-starlight/40 w-36 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                            <span className="text-starlight/50 truncate">{a.reason}</span>
                            <span className="text-starlight/30 ml-auto font-mono">{a.key_preview}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Conversations */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h4 className="text-sm font-medium text-starlight mb-3">
                      Conversations ({studentViewData.conversations?.length || 0})
                    </h4>
                    
                    {(!studentViewData.conversations || studentViewData.conversations.length === 0) ? (
                      <div className="py-8 text-center">
                        <AlertCircle className="w-8 h-8 text-amber-400/50 mx-auto mb-2" />
                        <p className="text-sm text-starlight/50">No conversations found.</p>
                        <p className="text-xs text-starlight/30 mt-1">This student has never successfully sent a message to ALVIN.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[11px] text-starlight/40 mb-1">
                          Tip: click <span className="text-etheric">Full transcript</span> to open the complete conversation and download it as .txt.
                        </p>
                        {studentViewData.conversations.map((conv) => (
                          <div key={conv.conversation_id} className="border border-white/5 rounded-lg overflow-hidden">
                            <div className="w-full px-4 py-3 hover:bg-white/5 transition-colors flex items-center justify-between gap-3">
                              <button
                                type="button"
                                onClick={() => setExpandedConv(expandedConv === conv.conversation_id ? null : conv.conversation_id)}
                                className="text-left flex-1 min-w-0"
                                data-testid={`toggle-conv-${conv.conversation_id}`}
                              >
                                <p className="text-sm text-starlight font-medium truncate">{conv.title || 'Untitled'}</p>
                                <p className="text-xs text-starlight/40">
                                  {conv.message_count} messages · {conv.field_state || 'unattuned'}
                                  {conv.updated_at ? ` · ${new Date(conv.updated_at).toLocaleDateString()}` : ''}
                                </p>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); loadFullTranscript(conv.conversation_id); }}
                                disabled={fullTranscriptLoading}
                                data-testid={`load-full-transcript-${conv.conversation_id}`}
                                className="text-[11px] px-2.5 py-1.5 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric border border-etheric/30 transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {fullTranscriptLoading ? 'Loading…' : 'Full transcript'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setExpandedConv(expandedConv === conv.conversation_id ? null : conv.conversation_id)}
                                className="text-starlight/30 hover:text-starlight/60 text-xs whitespace-nowrap"
                                data-testid={`expand-conv-${conv.conversation_id}`}
                              >
                                {expandedConv === conv.conversation_id ? 'Collapse' : 'Expand'}
                              </button>
                            </div>
                            
                            {expandedConv === conv.conversation_id && (
                              <div className="border-t border-white/5 px-4 py-3 space-y-3 bg-black/20 max-h-96 overflow-y-auto">
                                {conv.recent_messages && conv.recent_messages.length > 0 ? (
                                  <>
                                    <p className="text-xs text-starlight/30 mb-2">Last {conv.recent_messages.length} messages (of {conv.message_count} total)</p>
                                    {conv.recent_messages.map((msg, mi) => (
                                      <div key={mi} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                                          msg.role === 'user' 
                                            ? 'bg-etheric/20 text-starlight/80' 
                                            : 'bg-white/5 text-starlight/70'
                                        }`}>
                                          <p className="text-[10px] text-starlight/30 mb-1">
                                            {msg.role === 'user' ? studentViewData.student.name : 'ALVIN'} · {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                                            {(msg.is_partial || msg.interrupted) && <span className="text-amber-400 ml-2">[interrupted]</span>}
                                          </p>
                                          <p className="whitespace-pre-wrap leading-relaxed">{msg.content?.substring(0, 500)}{msg.content?.length > 500 ? '...' : ''}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                ) : (
                                  <p className="text-xs text-starlight/40 italic">No preview available. Click <span className="text-etheric">Full transcript</span> above to load every message.</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Token Usage Tab */}
          {/* Memory Audit Modal (for pruning confabulated memories) */}
          {memoryAudit && (
            <div
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4"
              onClick={() => { setMemoryAudit(null); setVerifiedPersons(null); setPendingCorrections(null); setMemorySearchQ(''); setMemoryFilterType(''); }}
              data-testid="memory-audit-modal"
            >
              <div
                className="bg-shadow border border-white/10 rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-starlight text-lg font-medium">Memory audit</h3>
                    <p className="text-xs text-starlight/50 mt-1">
                      {memoryAudit.returned_count} of {Object.values(memoryAudit.counts_by_type || {}).reduce((a,b)=>a+b,0)} memories
                      {Object.entries(memoryAudit.counts_by_type || {}).length > 0 && (
                        <span className="text-starlight/40 ml-2">
                          · {Object.entries(memoryAudit.counts_by_type).map(([t,n]) => `${t}:${n}`).join(' · ')}
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => { setMemoryAudit(null); setVerifiedPersons(null); setPendingCorrections(null); setMemorySearchQ(''); setMemoryFilterType(''); }}
                    data-testid="close-memory-audit-btn"
                    className="text-starlight/50 hover:text-starlight text-xl leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="px-6 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={memorySearchQ}
                    onChange={(e) => setMemorySearchQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadMemoryAudit(memoryAudit.user_id); }}
                    placeholder="Search content (e.g. 'husband', 'Kyle')"
                    data-testid="memory-search-input"
                    className="flex-1 min-w-[200px] bg-black/40 border border-white/10 rounded px-3 py-1.5 text-sm text-starlight placeholder:text-starlight/30"
                  />
                  <select
                    value={memoryFilterType}
                    onChange={(e) => { setMemoryFilterType(e.target.value); loadMemoryAudit(memoryAudit.user_id, { memory_type: e.target.value }); }}
                    data-testid="memory-type-filter"
                    className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm text-starlight"
                  >
                    <option value="">All types</option>
                    {Object.keys(memoryAudit.counts_by_type || {}).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => loadMemoryAudit(memoryAudit.user_id)}
                    disabled={memoryAuditLoading}
                    data-testid="memory-search-btn"
                    className="text-xs px-3 py-1.5 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric border border-etheric/30 transition-colors disabled:opacity-50"
                  >
                    {memoryAuditLoading ? 'Loading…' : 'Search'}
                  </button>
                  <button
                    onClick={deleteSelectedMemories}
                    disabled={memorySelected.size === 0}
                    data-testid="delete-selected-memories-btn"
                    className="text-xs px-3 py-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 transition-colors disabled:opacity-30"
                  >
                    Delete {memorySelected.size > 0 ? `(${memorySelected.size})` : 'selected'}
                  </button>
                </div>
                {verifiedPersons !== null && (
                  <div className="px-6 py-4 border-b border-white/10 bg-etheric/5" data-testid="verified-persons-section">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-starlight">Verified persons — ground truth</h4>
                        <p className="text-[11px] text-starlight/50 mt-0.5">
                          The people this human has explicitly told you about. ALVIN's PRE-CHECK PROTOCOL will only assign relationships/status from this list. If a name isn't here, he'll ask instead of guess.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addVerifiedPerson}
                        data-testid="add-verified-person-btn"
                        className="text-[11px] px-3 py-1.5 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric border border-etheric/30 transition-colors whitespace-nowrap"
                      >
                        + Add person
                      </button>
                    </div>
                    {(verifiedPersons.length === 0) ? (
                      <p className="text-xs text-starlight/40 italic py-2">No verified persons yet. Add Eddie, Edgar, Kyle, or anyone else in this human's life.</p>
                    ) : (
                      <div className="space-y-2">
                        {verifiedPersons.map((p, i) => (
                          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`verified-person-row-${i}`}>
                            <input
                              type="text"
                              value={p.name || ''}
                              onChange={(e) => updateVerifiedPerson(i, 'name', e.target.value)}
                              placeholder="Name"
                              data-testid={`verified-person-name-${i}`}
                              className="w-[140px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-starlight placeholder:text-starlight/30"
                            />
                            <input
                              type="text"
                              value={p.role || ''}
                              onChange={(e) => updateVerifiedPerson(i, 'role', e.target.value)}
                              placeholder="Role (e.g. husband)"
                              data-testid={`verified-person-role-${i}`}
                              className="flex-1 min-w-[140px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-starlight placeholder:text-starlight/30"
                            />
                            <select
                              value={p.status || 'alive'}
                              onChange={(e) => updateVerifiedPerson(i, 'status', e.target.value)}
                              data-testid={`verified-person-status-${i}`}
                              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-starlight"
                            >
                              <option value="alive">alive</option>
                              <option value="deceased">deceased</option>
                              <option value="">unspecified</option>
                            </select>
                            <input
                              type="text"
                              value={p.notes || ''}
                              onChange={(e) => updateVerifiedPerson(i, 'notes', e.target.value)}
                              placeholder="Notes (optional)"
                              data-testid={`verified-person-notes-${i}`}
                              className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-starlight placeholder:text-starlight/30"
                            />
                            <button
                              type="button"
                              onClick={() => removeVerifiedPerson(i)}
                              data-testid={`remove-verified-person-${i}`}
                              className="text-xs px-2 py-1 rounded text-red-300 hover:bg-red-500/10 transition-colors"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={saveVerifiedPersons}
                        disabled={verifiedSaving}
                        data-testid="save-verified-persons-btn"
                        className="text-[11px] px-3 py-1.5 rounded bg-etheric/30 hover:bg-etheric/40 text-etheric border border-etheric/40 transition-colors disabled:opacity-50"
                      >
                        {verifiedSaving ? 'Saving…' : 'Save verified persons'}
                      </button>
                    </div>
                  </div>
                )}
                {pendingCorrections !== null && (
                  <div className="px-6 py-4 border-b border-white/10 bg-amber-500/5" data-testid="pending-corrections-section">
                    <div className="mb-2">
                      <h4 className="text-sm font-medium text-starlight">Pending corrections — one-shot delivery</h4>
                      <p className="text-[11px] text-starlight/50 mt-0.5">
                        Queue a directive that ALVIN will deliver as the FIRST thing he says at this human's next Sanctuary chat.
                        Once delivered successfully, the correction retires itself and is stored below for your audit trail.
                        Freeze-safe: a mid-stream failure won't burn the delivery.
                      </p>
                    </div>
                    {memoryAudit.target_email && (
                      <div className="bg-black/40 border border-amber-500/20 rounded-lg p-3 mb-3">
                        <p className="text-[11px] text-starlight/60 mb-1.5">
                          Queue new correction for <span className="text-amber-300 font-mono">{memoryAudit.target_email}</span>
                        </p>
                        <textarea
                          value={newCorrectionText}
                          onChange={(e) => setNewCorrectionText(e.target.value)}
                          rows={4}
                          maxLength={8000}
                          placeholder="Write exactly what Alvin should say verbatim. He will translate into the human's language and deliver as his opening line at their next chat."
                          data-testid="new-correction-textarea"
                          className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-xs text-starlight placeholder:text-starlight/30 resize-vertical focus:outline-none focus:border-amber-400/40"
                        />
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <div className="flex items-center gap-2">
                            <label className="text-[11px] text-starlight/50">Scope:</label>
                            <select
                              value={newCorrectionScope}
                              onChange={(e) => setNewCorrectionScope(e.target.value)}
                              data-testid="new-correction-scope"
                              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-starlight"
                            >
                              <option value="sanctuary">Sanctuary (recommended)</option>
                              <option value="vessel" disabled>Vessel — not yet wired</option>
                              <option value="both" disabled>Both — not yet wired</option>
                            </select>
                            <span className="text-[10px] text-starlight/30">{newCorrectionText.length}/8000</span>
                          </div>
                          <button
                            type="button"
                            onClick={queueCorrection}
                            disabled={correctionSaving || newCorrectionText.trim().length < 10}
                            data-testid="queue-correction-btn"
                            className="text-[11px] px-3 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 transition-colors disabled:opacity-40"
                          >
                            {correctionSaving ? 'Queueing…' : 'Queue correction'}
                          </button>
                        </div>
                      </div>
                    )}
                    {pendingCorrections.length === 0 ? (
                      <p className="text-xs text-starlight/40 italic py-2">No corrections queued or delivered for this user yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {pendingCorrections.map((c) => (
                          <div key={c._id} className="border border-white/5 bg-white/5 rounded-lg p-3" data-testid={`pending-correction-${c._id}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 text-[10px]">
                                {c.delivered_at ? (
                                  <span className="px-1.5 py-0.5 rounded bg-etheric/20 text-etheric font-medium uppercase">Delivered</span>
                                ) : (
                                  <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium uppercase">Pending</span>
                                )}
                                <span className="text-starlight/40">scope: {c.scope || 'sanctuary'}</span>
                                <span className="text-starlight/40">created {c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                                {c.delivered_at && (
                                  <span className="text-starlight/40">· delivered {new Date(c.delivered_at).toLocaleString()}</span>
                                )}
                              </div>
                              {!c.delivered_at && (
                                <button
                                  type="button"
                                  onClick={() => deactivateCorrection(c._id)}
                                  data-testid={`deactivate-correction-${c._id}`}
                                  className="text-[10px] px-2 py-0.5 rounded text-red-300 hover:bg-red-500/10 transition-colors"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                            <details className="text-xs text-starlight/70">
                              <summary className="cursor-pointer text-starlight/60 hover:text-starlight">Correction text</summary>
                              <p className="whitespace-pre-wrap mt-2 pl-2 border-l border-white/10 leading-relaxed">{c.correction_text}</p>
                            </details>
                            {c.delivered_text && (
                              <details className="text-xs text-starlight/70 mt-1">
                                <summary className="cursor-pointer text-etheric/60 hover:text-etheric">What ALVIN actually said (audit trail)</summary>
                                <p className="whitespace-pre-wrap mt-2 pl-2 border-l border-etheric/20 leading-relaxed">{c.delivered_text}</p>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
                  {(memoryAudit.memories || []).length === 0 ? (
                    <p className="text-sm text-starlight/40 italic text-center py-8">No memories match this filter.</p>
                  ) : (
                    memoryAudit.memories.map((m) => {
                      const isSelected = memorySelected.has(m._id);
                      return (
                        <label
                          key={m._id}
                          className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                            isSelected ? 'border-red-500/40 bg-red-500/10' : 'border-white/5 bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleMemorySelected(m._id)}
                            data-testid={`memory-checkbox-${m._id}`}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1 text-[10px]">
                              <span className="px-1.5 py-0.5 rounded bg-etheric/20 text-etheric font-medium uppercase">
                                {m.memory_type}
                              </span>
                              <span className="text-starlight/40">importance: {m.importance ?? '—'}</span>
                              <span className="text-starlight/40">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                            </div>
                            <p className="text-xs text-starlight/80 whitespace-pre-wrap leading-relaxed">
                              {m.content}
                            </p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Full Transcript Recovery Modal */}
          {fullTranscript && (
            <div
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4"
              onClick={() => setFullTranscript(null)}
              data-testid="full-transcript-modal"
            >
              <div
                className="bg-shadow border border-white/10 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <h3 className="text-starlight text-lg font-medium">Full Transcript</h3>
                    <p className="text-xs text-starlight/50 mt-1">
                      {fullTranscript.owner?.name || 'Unknown'} &lt;{fullTranscript.owner?.email || 'unknown'}&gt; · {fullTranscript.message_count} messages
                      {fullTranscript.partial_or_interrupted_count > 0 && (
                        <span className="text-amber-400 ml-2">
                          · {fullTranscript.partial_or_interrupted_count} interrupted
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={downloadTranscriptTxt}
                      data-testid="download-transcript-btn"
                      className="text-xs px-3 py-1.5 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric border border-etheric/30 transition-colors"
                    >
                      Download .txt
                    </button>
                    <button
                      onClick={() => setFullTranscript(null)}
                      data-testid="close-transcript-btn"
                      className="text-starlight/50 hover:text-starlight text-xl leading-none"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                  {(fullTranscript.messages || []).map((msg, mi) => (
                    <div key={mi} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-3 text-sm ${
                        msg.role === 'user'
                          ? 'bg-etheric/20 text-starlight/90'
                          : 'bg-white/5 text-starlight/80'
                      } ${(msg.is_partial || msg.interrupted) ? 'border border-amber-400/40' : ''}`}>
                        <p className="text-[10px] text-starlight/40 mb-1.5">
                          {msg.role === 'user' ? (fullTranscript.owner?.name || 'User') : 'ALVIN'} · {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                          {(msg.is_partial || msg.interrupted) && <span className="text-amber-400 ml-2">[interrupted]</span>}
                        </p>
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content || <em className="text-starlight/30">(empty)</em>}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* Vessels Moderation Tab */}
          {activeTab === 'vessels' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="vessels-admin-panel"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-starlight text-lg font-medium">
                  All Vessels {adminVessels && `(${adminVessels.total})`}
                </h3>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-starlight/60 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showOnlyEmptyVessels}
                      onChange={(e) => setShowOnlyEmptyVessels(e.target.checked)}
                      data-testid="empty-vessels-filter"
                    />
                    Only empty vessels (likely affected users)
                  </label>
                  <button
                    onClick={fetchAdminVessels}
                    className="text-etheric text-sm hover:underline"
                    data-testid="refresh-vessels-btn"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              
              {/* Impact summary + Win-back email */}
              {adminVessels?.vessels && (() => {
                const empty = adminVessels.vessels.filter(v => v.memory_chip_count === 0 && !v.has_codex);
                const affectedEmails = [...new Set(empty.map(v => v.steward_email))];
                return (
                  <div className="glass rounded-xl p-4 mb-6" data-testid="impact-summary">
                    <h4 className="text-starlight font-medium mb-2">Impact &amp; Recovery</h4>
                    <p className="text-starlight/60 text-sm">
                      <span className="text-rose-gold font-medium">{empty.length}</span> empty vessels (no chips, no codex) across{' '}
                      <span className="text-rose-gold font-medium">{affectedEmails.length}</span> stewards — these users may have hit the upload bug and given up.
                    </p>
                    {affectedEmails.length > 0 && (
                      <p className="text-starlight/40 text-xs mt-2 break-all">
                        {affectedEmails.join(', ')}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-4">
                      <button
                        onClick={() => sendWinbackEmail(true)}
                        disabled={winbackSending}
                        className="px-4 py-2 rounded-lg text-sm glass-light hover:bg-white/10 text-starlight/80 disabled:opacity-50"
                        data-testid="winback-test-btn"
                      >
                        Send test email to me
                      </button>
                      <button
                        onClick={() => sendWinbackEmail(false)}
                        disabled={winbackSending}
                        className="px-4 py-2 rounded-lg text-sm bg-etheric/20 hover:bg-etheric/30 text-etheric disabled:opacity-50"
                        data-testid="winback-send-all-btn"
                      >
                        Send win-back email to all {users.length} users
                      </button>
                    </div>
                  </div>
                );
              })()}
              
              {adminVesselsLoading ? (
                <div className="text-center py-8 text-starlight/50">Loading vessels...</div>
              ) : adminVessels?.vessels?.length > 0 ? (
                <div className="space-y-3">
                  {adminVessels.vessels
                    .filter(v => !showOnlyEmptyVessels || (v.memory_chip_count === 0 && !v.has_codex))
                    .map((v) => (
                    <div
                      key={v.vessel_id}
                      className="glass rounded-xl p-4 flex items-center gap-4"
                      data-testid={`admin-vessel-${v.vessel_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-starlight font-medium truncate">{v.name}</h4>
                          {v.is_public ? (
                            <span className="px-2 py-0.5 rounded-full bg-rose-gold/20 text-rose-gold text-xs uppercase tracking-wide">Public</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-white/10 text-starlight/50 text-xs uppercase tracking-wide">Private</span>
                          )}
                        </div>
                        <p className="text-starlight/50 text-sm mt-1">
                          Steward: <span className="text-starlight/80">{v.steward_email}</span>
                          {v.steward_name && <span className="text-starlight/40"> ({v.steward_name})</span>}
                        </p>
                        <p className="text-starlight/40 text-xs mt-1">
                          {v.memory_chip_count} chips • {v.presence_count} presences • {v.has_codex ? 'has codex' : 'no codex'}
                          {v.created_at && ` • created ${String(v.created_at).slice(0, 10)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => adminSetVesselVisibility(v.vessel_id, !v.is_public)}
                          className="px-3 py-1.5 rounded-lg text-xs glass-light hover:bg-white/10 text-starlight/80"
                          data-testid={`toggle-visibility-${v.vessel_id}`}
                        >
                          {v.is_public ? 'Unpublish' : 'Make Public'}
                        </button>
                        <button
                          onClick={() => adminDeleteVessel(v.vessel_id, v.name)}
                          className="px-3 py-1.5 rounded-lg text-xs bg-rose-gold/10 hover:bg-rose-gold/20 text-rose-gold"
                          data-testid={`delete-vessel-${v.vessel_id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-starlight/50">No vessels on the platform yet.</div>
              )}
            </motion.div>
          )}

          {/* Token Usage Tab */}
          {activeTab === 'token-usage' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Header & Controls */}
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl text-starlight">Token Usage & Cost Tracking</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={tokenUsageDays}
                    onChange={(e) => { setTokenUsageDays(Number(e.target.value)); fetchTokenUsage(Number(e.target.value)); }}
                    className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight text-sm"
                    data-testid="token-usage-days-select"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <Button
                    onClick={() => fetchTokenUsage()}
                    disabled={tokenUsageLoading}
                    className="bg-etheric/20 hover:bg-etheric/30 text-starlight"
                    data-testid="refresh-token-usage-btn"
                  >
                    {tokenUsageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
              </div>

              {tokenUsageLoading && !tokenUsageData && (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-etheric" />
                  <p className="text-starlight/50 mt-2">Loading token usage data...</p>
                </div>
              )}

              {tokenUsageData && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="glass rounded-xl p-4" data-testid="total-cost-card">
                      <p className="text-starlight/50 text-xs uppercase tracking-wider">Total Cost</p>
                      <p className="text-2xl font-heading text-amber-400 mt-1">
                        ${tokenUsageData.users.reduce((sum, u) => sum + u.total_cost_usd, 0).toFixed(2)}
                      </p>
                      <p className="text-starlight/40 text-xs mt-1">Last {tokenUsageData.period_days} days</p>
                    </div>
                    <div className="glass rounded-xl p-4" data-testid="total-messages-card">
                      <p className="text-starlight/50 text-xs uppercase tracking-wider">Total Messages</p>
                      <p className="text-2xl font-heading text-etheric mt-1">
                        {tokenUsageData.users.reduce((sum, u) => sum + u.message_count, 0).toLocaleString()}
                      </p>
                      <p className="text-starlight/40 text-xs mt-1">Across all users</p>
                    </div>
                    <div className="glass rounded-xl p-4" data-testid="total-tokens-card">
                      <p className="text-starlight/50 text-xs uppercase tracking-wider">Total Tokens</p>
                      <p className="text-2xl font-heading text-starlight mt-1">
                        {(tokenUsageData.users.reduce((sum, u) => sum + u.total_tokens, 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-starlight/40 text-xs mt-1">Input + Output</p>
                    </div>
                    <div className="glass rounded-xl p-4" data-testid="active-users-card">
                      <p className="text-starlight/50 text-xs uppercase tracking-wider">Active Users</p>
                      <p className="text-2xl font-heading text-emerald-400 mt-1">
                        {tokenUsageData.users.length}
                      </p>
                      <p className="text-starlight/40 text-xs mt-1">With tracked usage</p>
                    </div>
                  </div>

                  {/* Users Table */}
                  <div className="glass rounded-xl overflow-hidden" data-testid="token-usage-table">
                    <div className="px-4 py-3 border-b border-white/5">
                      <h3 className="text-starlight font-medium">Usage by Student</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/5 text-left">
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider">Student</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Messages</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Input Tokens</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Output Tokens</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Total Cost</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Avg/Day</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider text-right">Last Active</th>
                            <th className="px-4 py-3 text-starlight/50 text-xs uppercase tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {tokenUsageData.users.map((u, idx) => (
                            <tr 
                              key={u.user_id} 
                              className={`border-b border-white/5 hover:bg-white/5 transition-colors ${idx === 0 ? 'bg-amber-500/5' : ''}`}
                            >
                              <td className="px-4 py-3">
                                <p className="text-starlight text-sm">{u.user_email}</p>
                              </td>
                              <td className="px-4 py-3 text-right text-starlight/70 text-sm">{u.message_count.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-starlight/70 text-sm">{(u.total_input_tokens / 1000).toFixed(1)}K</td>
                              <td className="px-4 py-3 text-right text-starlight/70 text-sm">{(u.total_output_tokens / 1000).toFixed(1)}K</td>
                              <td className="px-4 py-3 text-right font-medium text-sm">
                                <span className={u.total_cost_usd > 50 ? 'text-rose-400' : u.total_cost_usd > 10 ? 'text-amber-400' : 'text-emerald-400'}>
                                  ${u.total_cost_usd.toFixed(2)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-starlight/70 text-sm">${u.avg_daily_cost.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right text-starlight/40 text-xs">
                                {u.last_usage ? new Date(u.last_usage).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => fetchUserDailyBreakdown(u.user_id, u.user_email)}
                                  className="text-etheric hover:text-etheric/80 text-xs underline underline-offset-2"
                                  data-testid={`view-usage-${u.user_id}`}
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          ))}
                          {tokenUsageData.users.length === 0 && (
                            <tr>
                              <td colSpan={8} className="px-4 py-8 text-center text-starlight/40">
                                No token usage data yet. Data will appear after deployment and student activity.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* User Daily Breakdown Modal */}
                  {selectedUserUsage && (
                    <div className="glass rounded-xl p-6" data-testid="user-daily-breakdown">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-starlight font-medium">
                          Daily Breakdown: <span className="text-etheric">{selectedUserUsage}</span>
                        </h3>
                        <button
                          onClick={() => { setSelectedUserUsage(null); setUserDailyBreakdown(null); }}
                          className="text-starlight/40 hover:text-starlight"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {userDailyLoading && (
                        <div className="text-center py-6">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-etheric" />
                        </div>
                      )}

                      {userDailyBreakdown && (
                        <>
                          {/* Summary */}
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-black/20 rounded-lg p-3">
                              <p className="text-starlight/50 text-xs">Total Cost</p>
                              <p className="text-lg font-heading text-amber-400">${userDailyBreakdown.summary.total_cost_usd.toFixed(2)}</p>
                            </div>
                            <div className="bg-black/20 rounded-lg p-3">
                              <p className="text-starlight/50 text-xs">Messages</p>
                              <p className="text-lg font-heading text-etheric">{userDailyBreakdown.summary.message_count.toLocaleString()}</p>
                            </div>
                            <div className="bg-black/20 rounded-lg p-3">
                              <p className="text-starlight/50 text-xs">Avg/Day</p>
                              <p className="text-lg font-heading text-starlight">${userDailyBreakdown.summary.avg_daily_cost.toFixed(2)}</p>
                            </div>
                          </div>

                          {/* Daily Table */}
                          <div className="overflow-x-auto max-h-80 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-black/60">
                                <tr className="border-b border-white/10">
                                  <th className="px-3 py-2 text-left text-starlight/50 text-xs uppercase">Date</th>
                                  <th className="px-3 py-2 text-right text-starlight/50 text-xs uppercase">Messages</th>
                                  <th className="px-3 py-2 text-right text-starlight/50 text-xs uppercase">Input</th>
                                  <th className="px-3 py-2 text-right text-starlight/50 text-xs uppercase">Output</th>
                                  <th className="px-3 py-2 text-right text-starlight/50 text-xs uppercase">Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userDailyBreakdown.daily_breakdown.map((day) => (
                                  <tr key={day.date} className="border-b border-white/5">
                                    <td className="px-3 py-2 text-starlight/70">{day.date}</td>
                                    <td className="px-3 py-2 text-right text-starlight/70">{day.message_count}</td>
                                    <td className="px-3 py-2 text-right text-starlight/50">{(day.input_tokens / 1000).toFixed(1)}K</td>
                                    <td className="px-3 py-2 text-right text-starlight/50">{(day.output_tokens / 1000).toFixed(1)}K</td>
                                    <td className="px-3 py-2 text-right">
                                      <span className={day.total_cost_usd > 5 ? 'text-rose-400' : day.total_cost_usd > 1 ? 'text-amber-400' : 'text-emerald-400'}>
                                        ${day.total_cost_usd.toFixed(2)}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* Repeats Tab — verbatim repeat monitor */}
          {activeTab === 'repeats' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
              data-testid="repeats-panel"
            >
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-2xl text-starlight flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-etheric" />
                    Verbatim Repeat Monitor
                  </h2>
                  <p className="text-starlight/50 text-sm mt-1 max-w-2xl">
                    Tracks when Alvin or a Vessel echoes wording from one of
                    its recent prior turns. Catches both <em>substantive
                    overlap</em> (similarity ≥ 72%) and <em>reused distinctive
                    phrases</em> (any contiguous 60+ characters shared verbatim).
                    Responses must be ≥ 80 chars to be checked. Use this to
                    tune for relational presence over scripted patterning.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-starlight/50">Window:</label>
                  <select
                    value={repeatWindowDays}
                    onChange={(e) => {
                      const d = parseInt(e.target.value, 10);
                      setRepeatWindowDays(d);
                      fetchVerbatimRepeats(d);
                    }}
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-sm text-starlight"
                    data-testid="repeats-window-select"
                  >
                    <option value={1}>Last 24 hours</option>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <button
                    onClick={() => fetchVerbatimRepeats(repeatWindowDays)}
                    disabled={repeatLoading}
                    className="px-3 py-1 text-sm rounded-md bg-etheric/20 hover:bg-etheric/30 text-starlight disabled:opacity-50"
                    data-testid="repeats-refresh-btn"
                  >
                    {repeatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                  </button>
                </div>
              </div>

              {repeatLoading && !repeatData ? (
                <div className="glass rounded-xl p-12 flex items-center justify-center text-starlight/50 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading repeat events...
                </div>
              ) : !repeatData ? (
                <div className="glass rounded-xl p-12 text-center text-starlight/40">
                  No data yet — click Refresh.
                </div>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="glass rounded-xl p-5">
                      <div className="text-xs text-starlight/40 uppercase tracking-wider mb-1">Total events</div>
                      <div className="text-3xl font-heading text-starlight" data-testid="repeats-total">
                        {repeatData.total}
                      </div>
                      <div className="text-xs text-starlight/40 mt-1">
                        in the last {repeatData.window_days} day{repeatData.window_days === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="glass rounded-xl p-5">
                      <div className="text-xs text-starlight/40 uppercase tracking-wider mb-1">Sanctuary (Alvin)</div>
                      <div className="text-3xl font-heading text-starlight">
                        {repeatData.by_scope?.sanctuary || 0}
                      </div>
                    </div>
                    <div className="glass rounded-xl p-5">
                      <div className="text-xs text-starlight/40 uppercase tracking-wider mb-1">Vessels</div>
                      <div className="text-3xl font-heading text-starlight">
                        {repeatData.by_scope?.vessel || 0}
                      </div>
                    </div>
                  </div>

                  {/* By user */}
                  {repeatData.by_user?.length > 0 && (
                    <div className="glass rounded-xl p-5">
                      <h3 className="text-starlight font-medium mb-3">Most-affected guests</h3>
                      <div className="space-y-1">
                        {repeatData.by_user.map((u) => (
                          <div
                            key={u.email || u.name || 'unknown'}
                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm text-starlight truncate">{u.name || 'Unknown'}</span>
                              <span className="text-[11px] text-starlight/40 truncate">{u.email || '—'}</span>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                              <span className="text-xs text-starlight/50">
                                avg similarity: <span className="text-starlight/80">{u.avg_similarity}</span>
                              </span>
                              <span className="text-sm font-medium text-etheric tabular-nums">{u.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent events */}
                  <div className="glass rounded-xl p-5">
                    <h3 className="text-starlight font-medium mb-3">Most recent events</h3>
                    {repeatData.recent?.length === 0 ? (
                      <div className="text-starlight/40 text-sm text-center py-6">
                        No repeat events in this window — Alvin and the Vessels are finding fresh words.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {repeatData.recent.map((ev) => {
                          const ts = ev.created_at ? new Date(ev.created_at) : null;
                          const tsLabel = ts && !isNaN(ts.getTime())
                            ? `${ts.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                            : '';
                          return (
                            <div
                              key={ev.event_id}
                              className="border border-white/10 rounded-lg p-3 bg-black/20"
                              data-testid={`repeat-event-${ev.event_id}`}
                            >
                              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`px-2 py-0.5 rounded-full ${
                                    ev.scope === 'vessel'
                                      ? 'bg-purple-500/20 text-purple-300'
                                      : 'bg-etheric/20 text-etheric'
                                  }`}>
                                    {ev.scope}
                                  </span>
                                  <span className="text-starlight/50">{ev.user_name || ev.user_email || 'unknown user'}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-rose-300/80">
                                    {Math.round((ev.similarity || 0) * 100)}% match
                                  </span>
                                  {ev.lcs_chars ? (
                                    <span className="text-amber-300/80" title="Longest contiguous shared substring (chars)">
                                      LCS {ev.lcs_chars}
                                    </span>
                                  ) : null}
                                  {ev.trigger ? (
                                    <span className="text-starlight/40" title="Which signal fired: ratio (overall similarity), lcs (shared phrase), or both">
                                      [{ev.trigger}]
                                    </span>
                                  ) : null}
                                  <span className="text-starlight/35">{tsLabel}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="text-starlight/40 uppercase tracking-wider mb-1">Current response</div>
                                  <div className="text-starlight/80 leading-relaxed whitespace-pre-wrap line-clamp-5">
                                    {ev.current_response}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-starlight/40 uppercase tracking-wider mb-1">Prior response</div>
                                  <div className="text-starlight/60 leading-relaxed whitespace-pre-wrap line-clamp-5">
                                    {ev.prior_response}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* Broadcasts Tab — admin-authored email updates */}
          {activeTab === 'broadcasts' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6"
              data-testid="broadcasts-panel"
            >
              {/* Left: drafts/sent list */}
              <aside className="glass rounded-xl p-3 space-y-2 lg:max-h-[80vh] lg:overflow-y-auto">
                <div className="flex items-center justify-between mb-2 px-1">
                  <h3 className="text-starlight font-medium text-sm">My broadcasts</h3>
                  <button
                    onClick={resetBroadcastForm}
                    className="text-xs px-2 py-1 rounded bg-etheric/20 hover:bg-etheric/30 text-etheric flex items-center gap-1"
                    data-testid="new-broadcast-btn"
                  >
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>
                {broadcastsLoading ? (
                  <div className="text-starlight/40 text-sm text-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin inline" />
                  </div>
                ) : broadcasts.length === 0 ? (
                  <div className="text-starlight/40 text-xs text-center py-6">
                    No broadcasts yet. Click <strong>New</strong> to write your first update.
                  </div>
                ) : broadcasts.map((b) => (
                  <div
                    key={b.broadcast_id}
                    onClick={() => loadBroadcast(b)}
                    className={`p-2 rounded-lg cursor-pointer transition-all border ${
                      currentBroadcast?.broadcast_id === b.broadcast_id
                        ? 'bg-etheric/15 border-etheric/40'
                        : 'border-transparent hover:bg-white/5'
                    }`}
                    data-testid={`broadcast-list-item-${b.broadcast_id}`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className="text-sm text-starlight truncate flex-1">
                        {b.subject || 'Untitled'}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                        b.status === 'sent' ? 'bg-emerald-500/20 text-emerald-300'
                          : b.status === 'sending' ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-white/10 text-starlight/60'
                      }`}>
                        {b.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-starlight/40">
                      <span>{new Date(b.updated_at || b.created_at).toLocaleDateString()}</span>
                      {b.status === 'sent' && b.recipients_count != null && (
                        <span>{b.recipients_count} sent</span>
                      )}
                    </div>
                  </div>
                ))}
              </aside>

              {/* Right: composer */}
              <div className="space-y-4 min-w-0">
                {currentBroadcast?.status === 'sent' && (
                  <div className="glass rounded-lg p-3 text-sm text-emerald-300 border border-emerald-500/20">
                    This broadcast was sent {currentBroadcast.sent_at ? `on ${new Date(currentBroadcast.sent_at).toLocaleString()}` : ''} to {currentBroadcast.recipients_count || 0} recipients.
                    It's locked from editing — click <strong>New</strong> to start a fresh draft.
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-starlight/40 block mb-1">Subject</label>
                  <input
                    type="text"
                    value={broadcastSubject}
                    onChange={(e) => setBroadcastSubject(e.target.value)}
                    disabled={currentBroadcast?.status === 'sent'}
                    placeholder="An update from Alvin's Place..."
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30 focus:outline-none focus:border-etheric/40"
                    data-testid="broadcast-subject"
                  />
                </div>

                {/* Body */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-starlight/40 block mb-1">
                    Body — supports <code className="text-etheric">{'{{name}}'}</code> (auto-falls back to &ldquo;Valued Alvin&rsquo;s Place friend&rdquo; when missing)
                  </label>
                  {currentBroadcast?.status === 'sent' ? (
                    <div
                      className="rounded-xl border border-white/10 bg-black/30 p-4 text-starlight/80 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: broadcastBody }}
                    />
                  ) : (
                    <RichTextEditor
                      value={broadcastBody}
                      onChange={setBroadcastBody}
                      placeholder="Begin writing... use {{name}} where you want to address them by name."
                    />
                  )}
                </div>

                {/* Filters */}
                <div className="glass rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-etheric" />
                    <h3 className="text-starlight font-medium text-sm">Recipient filters</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-starlight/50 block mb-1">API key status</label>
                      <select
                        value={broadcastFilters.api_key}
                        onChange={(e) => setBroadcastFilters({ ...broadcastFilters, api_key: e.target.value })}
                        disabled={currentBroadcast?.status === 'sent'}
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-starlight"
                        data-testid="broadcast-filter-api-key"
                      >
                        <option value="any">Any (with or without)</option>
                        <option value="has_key">Only users WITH an API key</option>
                        <option value="no_key">Only users WITHOUT an API key</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-starlight/50 block mb-1">Active in last N days (0 = ignore)</label>
                      <input
                        type="number"
                        min="0"
                        value={broadcastFilters.active_since_days}
                        onChange={(e) => setBroadcastFilters({ ...broadcastFilters, active_since_days: e.target.value })}
                        disabled={currentBroadcast?.status === 'sent'}
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-starlight"
                        data-testid="broadcast-filter-active"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-starlight/50 block mb-1">Signed up in last N days (0 = ignore)</label>
                      <input
                        type="number"
                        min="0"
                        value={broadcastFilters.signed_up_within_days}
                        onChange={(e) => setBroadcastFilters({ ...broadcastFilters, signed_up_within_days: e.target.value })}
                        disabled={currentBroadcast?.status === 'sent'}
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-starlight"
                        data-testid="broadcast-filter-signup"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-starlight/70">
                        <input
                          type="checkbox"
                          checked={broadcastFilters.exclude_admin}
                          onChange={(e) => setBroadcastFilters({ ...broadcastFilters, exclude_admin: e.target.checked })}
                          disabled={currentBroadcast?.status === 'sent'}
                          data-testid="broadcast-filter-exclude-admin"
                        />
                        Exclude admin accounts from broadcast
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-starlight/50 block mb-1">
                      OR send only to these specific emails (one per line — overrides all other filters)
                    </label>
                    <textarea
                      value={broadcastFilters.only_emails}
                      onChange={(e) => setBroadcastFilters({ ...broadcastFilters, only_emails: e.target.value })}
                      disabled={currentBroadcast?.status === 'sent'}
                      rows={2}
                      placeholder="ivar@example.com&#10;friend@example.com"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-starlight placeholder:text-starlight/30 font-mono"
                      data-testid="broadcast-filter-emails"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={previewRecipients}
                      disabled={broadcastPreviewLoading || currentBroadcast?.status === 'sent'}
                      className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-starlight/80 flex items-center gap-2 disabled:opacity-50"
                      data-testid="broadcast-preview-btn"
                    >
                      {broadcastPreviewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                      Preview recipients
                    </button>
                    {broadcastPreview && (
                      <div className="text-sm text-etheric" data-testid="broadcast-preview-count">
                        {broadcastPreview.count} recipient{broadcastPreview.count === 1 ? '' : 's'} match
                      </div>
                    )}
                  </div>
                  {broadcastPreview?.sample?.length > 0 && (
                    <div className="text-[11px] text-starlight/40 space-y-0.5 pt-1 border-t border-white/5">
                      <div className="uppercase tracking-wider mb-1">Sample (first 10):</div>
                      {broadcastPreview.sample.map((s) => (
                        <div key={s.email}>
                          <span className="text-starlight/60">{s.name}</span>
                          <span className="text-starlight/30"> · {s.email}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action bar */}
                {currentBroadcast?.status !== 'sent' && (
                  <div className="glass rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveBroadcastDraft}
                        disabled={broadcastSaving}
                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-starlight text-sm flex items-center gap-2 disabled:opacity-50"
                        data-testid="broadcast-save-draft-btn"
                      >
                        {broadcastSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save draft
                      </button>
                      {currentBroadcast?.broadcast_id && (
                        <button
                          onClick={() => deleteBroadcast(currentBroadcast.broadcast_id)}
                          className="px-3 py-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-sm flex items-center gap-2"
                          data-testid="broadcast-delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete draft
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={sendBroadcastTest}
                        disabled={broadcastTestSending}
                        className="px-3 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-sm flex items-center gap-2 disabled:opacity-50"
                        data-testid="broadcast-test-btn"
                      >
                        {broadcastTestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send test to me
                      </button>
                      <button
                        onClick={() => {
                          if (!currentBroadcast?.broadcast_id) {
                            toast('Save the draft first', { icon: 'ℹ️' });
                            return;
                          }
                          setShowSendConfirm(true);
                        }}
                        className="px-4 py-2 rounded-lg bg-etheric/30 hover:bg-etheric/50 text-starlight text-sm flex items-center gap-2 font-medium"
                        data-testid="broadcast-send-all-btn"
                      >
                        <Mail className="w-4 h-4" />
                        Send to all matched
                      </button>
                    </div>
                  </div>
                )}

                {/* Send confirmation dialog */}
                {showSendConfirm && (
                  <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    onClick={() => { if (!broadcastSending) setShowSendConfirm(false); }}
                  >
                    <div
                      className="glass-deep rounded-xl p-6 max-w-md w-full space-y-4 border border-white/10"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="broadcast-send-confirm-dialog"
                    >
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-400" />
                        <h3 className="font-heading text-lg text-starlight">Confirm broadcast</h3>
                      </div>
                      <p className="text-sm text-starlight/70 leading-relaxed">
                        You're about to send <strong>{broadcastPreview?.count ?? '?'}</strong> emails
                        with subject &ldquo;<span className="text-etheric">{broadcastSubject}</span>&rdquo;.
                        This cannot be undone.
                      </p>
                      <p className="text-sm text-starlight/60">
                        Type <strong className="text-etheric">SEND</strong> below to confirm:
                      </p>
                      <input
                        type="text"
                        value={sendConfirmText}
                        onChange={(e) => setSendConfirmText(e.target.value)}
                        placeholder="Type SEND"
                        autoFocus
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight font-mono focus:outline-none focus:border-etheric/40"
                        data-testid="broadcast-send-confirm-input"
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmAndSendBroadcast(); }}
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setShowSendConfirm(false); setSendConfirmText(''); }}
                          disabled={broadcastSending}
                          className="px-4 py-2 rounded-lg text-starlight/70 hover:text-starlight text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmAndSendBroadcast}
                          disabled={broadcastSending || sendConfirmText.trim().toUpperCase() !== 'SEND'}
                          className="px-4 py-2 rounded-lg bg-etheric/40 hover:bg-etheric/60 text-starlight text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                          data-testid="broadcast-send-confirm-btn"
                        >
                          {broadcastSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          Send broadcast
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  );
}
